// SceneryRenderer — Set13 P5: the 2D render pass for procedural scenery (§3.6).
//
// ONE InstancedMesh per kind: variant carried as a per-instance attribute indexing the atlas
// cell, so one draw call per kind regardless of instance or tile count. Two passes (renderOrder
// within the transparent pass):
//   1. SHADOW pass — a separate InstancedMesh per kind, multiply-blended, depth-write OFF,
//      renderOrder above the terrain (which is transparent, depthWrite false) but below the
//      tree pass. The quad centre is shifted down-light by the measured crown height
//      (`variantMaxZ * L.xy / L.z`); the teardrop SDF re-orients with the light with no atlas
//      re-bake.
//   2. TREE pass — alpha-test (discard in the fragment shader) + depth-write ON, depthFunc
//      GreaterEqual (higher z = crown closer to the top-down camera), so the taller crown
//      occludes the shorter with NO sorting and NO terrain data (§4.5).
//
// Both mirror the terrain conventions (§4.8): the same `u_terrainRegions` AABB clip and the same
// `u_opacity`. The atlas is baked by `ImpostorBaker` off-frame and cached by its §3.5 signature;
// it re-bakes only when that signature changes (a kind/light edit).
//
// Budget (§3.7): zoom-bucketed tile size + rank thinning + per-tile/per-total caps, all checked
// BEFORE allocation — the no-crash invariant. Tile size is a pure perf knob (§3.3), so re-bucketing
// changes buffer packing and nothing about where objects are.
import * as THREE from "three";
import {observer_mgl_r3} from "../../../Utils/ThreeJS/General.js";
import {Invalidate3JS} from "../../../Utils/ThreeJS/Invalidate3JS.js";
import {useThree, useFrame} from "@react-three/fiber";
import {useRef, useEffect} from "react";
import {mapZIndexes} from "../../../UI/Maps/MapUI/MapSurface/MapZIndexes.js";
import {GetProcSceneryKindsByID, ResolveSceneryKind} from "../../../Server/DB/mapObjects/SceneryAccessors.js";
import {deriveVariants} from "../SceneryVariants.js";
import {ImpostorBaker, sceneryBakeSignature, kindTextureTypeIDs, type BakeLightRig, type SceneryBakeResult} from "../Bake/ImpostorBaker.js";
// Set15 P7 — the shared material builder + geometry converter (the SAME materials the bake uses,
// so the near-tier real mesh and the far-tier impostor differ only in resolution).
import {buildSceneryMaterials, partToGeometry, type SceneryMaterials} from "../Bake/SceneryMaterials.js";
import {preloadTextures, texturesReady} from "../Bake/SceneryTextures.js";
import {placeSceneryTile, type SceneryInstance} from "../Placement/SceneryPlacer.js";
import {collectSceneryRegions, type SceneryRegion} from "../Placement/SceneryRegions.js";
import {SceneryBudgetConfig, SceneryBudgetTracker, estimateAtlasBytes, resolveBudgetNumber, tileSizeForMpp} from "../@SceneryBudgetConfig.js";
import {GetTerrainSceneriesMods} from "../../../Server/DB/mapObjects/$mapObject.js";
import {TREE_VERTEX_GLSL, TREE_FRAGMENT_GLSL, SHADOW_VERTEX_GLSL, SHADOW_FRAGMENT_GLSL, BILLBOARD3D_VERTEX_GLSL} from "./SceneryShaders.js";
import type {ResolvedSceneryKind} from "../SceneryResolverCore.js";
import type {SceneryMesh, SceneryVariant} from "../@SceneryTypes.js";
import {GetMapObjects} from "../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld} from "../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../Server/DB/mapObjects/@MapObject.js";
import {ResolveMapObjectsForTerrain} from "../../Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Resolver.js";
import {Mod_TerrainDefinition, TerrainHeightShadingConfig} from "../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainDefinition.js";
import {computeLightDirJS} from "../../Materials/Compose/LightingStage.js";
import {resolveViewInfo, resolveSceneryMaxDistance} from "../../Heights/TerrainViewInfo.js";
import {terrainQuadtree_current} from "../../Heights/TerrainRenderer.js";
import {terrainShadowLight_current} from "../../Heights/TerrainShadows.js";
import type {MapSettings_View3D} from "../../../Store/main/maps/@MapState/MapSettings.js";
import {resolveActiveViewpoint} from "../../Heights/ViewpointCamera.js";
// Set15 P5 debug — push the live scenery render state to `window.lfSceneryDebug` (agent debugging aid).
import {sceneryDebugUpdate} from "./SceneryDebug.js";
// Set15.5 — per-frame instanced/billboarded tree counts for the Stats panel.
import {sceneryRenderStats} from "./SceneryRenderStats.js";
// Set15.5 — the pure nearest-first ring tile enumeration (see its module docblock).
import {enumerateSceneryTiles} from "./SceneryTileEnumeration.js";
// 2026-08-28 — high-level scenery pipeline timing (placement vs fill hotspots).
import {timeIt, timeStmt} from "./SceneryTiming.js";
// 2026-08-28 — SHORT cache-key hashing. The tile-union cache key previously embedded the FULL
// `regionsSignature` + `kindUnionSig` strings (kilobytes of full-precision world polylines +
// every resolved kind's params). V8 re-hashes the whole string on every `Map.get(key)`, so the
// 1080 lookups/frame each hashed a kilobyte blob → `stmt.cacheGet` was 94% of the tileLoop (the
// real zoom-out hotspot). Hashing the two signatures to a compact 32-bit token (FNV-1a, see
// SceneryHash) makes the key short → `Map.get` hashes ~30 bytes instead of ~1 KB. Invalidation is
// preserved: the hash changes iff the signature content changes (the astronomically-rare 32-bit
// collision merely returns a stale cached tile, which the LRU evicts + the signature-gate rebuilds).
import {hashString32} from "../SceneryHash.js";
import {RunInAction_Set} from "web-vcore";

// The scenery meshes sit above the terrain (-1); crown-overlap ordering is handled entirely by
// the depth key + GreaterEqual depth func, so the base z just needs to be above terrain.
const SCENERY_TERRAIN_Z = mapZIndexes.sceneryZ_start;
// renderOrder (transparent pass): terrain chunks at 0; shadows above them, trees above the
// shadows (shadow first so its multiply lands on terrain, then trees write depth).
const SHADOW_RENDER_ORDER = 1;
const TREE_RENDER_ORDER = 2;
/** The crown-top (metres) that maps to `mapZIndexes.sceneryZ_end` — the top of the scenery
 *  depth-ordering band. The built-in "Large" presets' crown-tops top out ~26 m (Oak/Aspen Large);
 *  reserve headroom for per-instance scale jitter + user kinds. A taller crown would land past
 *  `sceneryZ_end` (soft cap) — an acceptable edge case for out-of-range authored sizes. */
const SCENERY_MAX_CROWN_HEIGHT = 30;
/** Maps the per-tree crown-top height (metres, the `instanceDepthKey`) onto the scenery z-band
 *  `[sceneryZ_start, sceneryZ_end]`. Was 0.02 (all trees collapsed into a ~0.3-unit z-slice that
 *  a 24-bit depth buffer over far=2000 could not resolve → whole-tree depth-culling on hardware
 *  GPU; SwiftShader's higher-precision depth survived, hence the renderer-dependent count). With
 *  the 10-100 band and ~3 units/metre, distinct crown heights get real depth-buffer separation. */
const TREE_DEPTH_SCALE = (mapZIndexes.sceneryZ_end - mapZIndexes.sceneryZ_start) / SCENERY_MAX_CROWN_HEIGHT;
// Max active TerrainRegion AABBs for the clip uniform array (must match the shader's constant).
const TERRAIN_REGION_CLIP_MAX = 16;

// ⚠ TEMP DEBUG FLAG (2026-08-15) — diagnostic logging for the "scenery disappears on kind edit"
// issue. Set to true, refresh, scroll around, watch the console, then set back to false.
const SCENERY_DEBUG = false;
const debug = (...args: unknown[]): void => { if (SCENERY_DEBUG) console.log("[SceneryDebug]", ...args); };

/** Set17 — the resolved-KIND dimension of the tile-union cache key. The region signature
 *  (`regionsSignature`) covers the region FEATURE overrides (incl. kinds+weights); this covers the
 *  RESOLVED kind defaults (spacing/presence/seed/jitter/collision) the placer reads per region via
 *  the `resolveKind`/`resolveVariants` closures. A kind-DEFAULT catalogue edit changes this even
 *  though the features are unchanged, and must invalidate the whole tile-union cache. */
function resolvedKindsSignature(resolvedKinds: ReadonlyMap<string, ResolvedSceneryKind>): string {
	const parts: string[] = [];
	for (const [id, r] of [...resolvedKinds].sort((a, b)=>a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)) {
		const c = r.collision;
		parts.push(`${id}:${r.spacing},${r.presence},${r.seed},${r.footprintRadius}` +
			`${r.jitter?.rotation ?? ""}/${r.jitter?.scale_min ?? ""}/${r.jitter?.scale_max ?? ""}/${r.jitter?.tintSwing ?? ""}` +
			`:${c.trunk}/${c.crown == null ? "n" : c.crown}`);
	}
	return parts.join("|");
}

/** A per-kind render bundle: its atlas + the two InstancedMeshes (shadow + tree) + material
 *  handles. One draw call per pass per kind. */
interface KindRenderState {
	resolved: ResolvedSceneryKind;
	bake: SceneryBakeResult;
	atlasTexture: THREE.Texture;
	treeMesh: THREE.InstancedMesh;
	shadowMesh: THREE.InstancedMesh;
	/** Set15 P4 (§3.8) — the 3D cylindrical-billboard InstancedMesh (only in perspective mode;
	 *  null in 2D). Renders the PITCH-baked atlas on vertical quads positioned at the terrain
	 *  height; replaces the 2D tree+shadow pair (no depth key, no shadow pass in v1). */
	billboardMesh: THREE.InstancedMesh | null;
	/** Set15 P7 (§3.8) — the 3D NEAR tier: real `InstancedMesh` per (kind, material-kind,
	 *  variant), drawn for instances within `nearMeshMaxDistance` of the eye. The far-tier
	 *  billboard (`billboardMesh`) carries everything beyond. Built lazily from the generated
	 *  variant meshes the bake produced; `null` in 2D. */
	nearMeshes: NearTierVariantState[] | null;
	/** True when this kind's render state was created for the perspective 3D camera (dictates
	 *  which mesh `fillKindInstances` fills + how). */
	isPerspective: boolean;
	bakeSignature: string;
	/** Atlas bytes this kind's bake occupies (for the global `maxAtlasBytes` guard — released on dispose). */
	atlasBytes: number;
	/** Reusable scratch for filling instance matrices. */
	_matrix: THREE.Matrix4;
	_quat: THREE.Quaternion;
	_pos: THREE.Vector3;
	_scl: THREE.Vector3;
	_axis: THREE.Vector3;
}

/** Set15 P7 — the near-tier render state for ONE variant: a real bark `InstancedMesh` + a real
 *  leaf `InstancedMesh`, sharing that variant's generated geometry + materials (the same
 *  `buildSceneryMaterials` the bake uses). One per variant, so an instance's `variantIndex`
 *  selects which mesh pair carries it. */
interface NearTierVariantState {
	/** The generated mesh this variant's geometry came from (for shared disposal bookkeeping). */
	mesh: SceneryMesh;
	/** The variant index this pair renders (selects the per-instance matrix buffer). */
	variantIndex: number;
	bark: THREE.InstancedMesh;
	leaf: THREE.InstancedMesh;
	/** Shared per-variant geometry (one bark + one leaf BufferGeometry, referenced by both the
	 *  mesh and its dispose). */
	geoBark: THREE.BufferGeometry;
	geoLeaf: THREE.BufferGeometry;
	/** Per-variant materials (shared with the bake). */
	materials: SceneryMaterials;
}

/** The active TerrainRegion AABBs, filtered to active terrain IDs — mirrors TerrainRenderer so
 *  the scenery clip agrees with the terrain clip (§4.8). */
function activeTerrainRegionAABBs(mapObjects: MapObject[], terrainMods: Mod_TerrainDefinition[]): {minX: number; minY: number; maxX: number; maxY: number}[] {
	const activeIDs = new Set(terrainMods.map(m=>m.terrainID));
	const aabbs: {minX: number; minY: number; maxX: number; maxY: number}[] = [];
	for (const obj of mapObjects) {
		for (const mod of obj.modifiers ?? []) {
			if (!mod.enabled || !mod.terrainRegion) continue;
			if (!activeIDs.has(mod.terrainRegion.terrainID)) continue;
			const pos = ResolveObjPosInWorld(obj);
			const size = obj.shape_size;
			if (!pos || !size) continue;
			aabbs.push({minX: pos.x - size.x / 2, minY: pos.y - size.y / 2, maxX: pos.x + size.x / 2, maxY: pos.y + size.y / 2});
		}
	}
	return aabbs;
}

/** Collect the enabled terrain-definition mods (mirrors TerrainRenderer). */
function terrainModsOf(mapObjects: MapObject[]): Mod_TerrainDefinition[] {
	const mods: Mod_TerrainDefinition[] = [];
	for (const obj of mapObjects) {
		for (const mod of obj.modifiers ?? []) {
			if (mod.enabled && mod.terrainDefinition) mods.push(mod.terrainDefinition);
		}
	}
	return mods;
}

/** Upload a bake result's pixel buffer to a THREE texture (top-left origin — flipY false). */
function bakeToTexture(bake: SceneryBakeResult): THREE.Texture {
	const tex = new THREE.DataTexture(bake.pixels, bake.atlasWidth, bake.atlasHeight);
	tex.flipY = false;
	tex.magFilter = THREE.LinearFilter;
	tex.minFilter = THREE.LinearFilter;
	tex.unpackAlignment = 1;
	// Set14 P7 §4.11 REVISED (2026-08-17) — the atlas holds LINEAR values (three forces
	// NoToneMapping + linear output for any non-XR render target, so the bake's
	// MeshStandardMaterial encodes nothing). Mark it LinearSRGBColorSpace EXPLICITLY so three
	// does NOT decode on sample (the tree quad applies the tone-map + sRGB-encode at the final
	// display write instead). Relying on the `NoColorSpace` default would work, but an explicit
	// linear declaration documents the intent and is robust to any future default change.
	tex.colorSpace = THREE.LinearSRGBColorSpace;
	tex.needsUpdate = true;
	return tex;
}

/** A cached per-tile placement UNION (Set17 §4): one single-pass-per-tile placement holds every
 *  kind's instances for a tile. Shared across kinds, so the arbiter + margin run once per tile,
 *  not once per kind. */
interface SceneryTileUnion {
	/** kindID → the placed instances for this tile. */
	instancesByKind: Map<string, SceneryInstance[]>;
	lastUsed: number;
}

/** LRU-evict a tile-union cache beyond the live-tile cap (degrade, never allocate past cap). */
function evictTileCache(cache: Map<string, SceneryTileUnion>, maxLiveTiles: number): void {
	if (cache.size <= maxLiveTiles) return;
	const entries = [...cache.entries()].sort((a, b)=>a[1].lastUsed - b[1].lastUsed);
	for (let i = 0; i < entries.length - maxLiveTiles; i++) cache.delete(entries[i][0]);
}

/** Create a quad InstancedBufferGeometry (unit square, -1..1, with UVs for the tree pass). */
function quadGeometry(withUV: boolean): THREE.InstancedBufferGeometry {
	const geo = new THREE.InstancedBufferGeometry();
	geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
	if (withUV) geo.setAttribute("uv", new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
	geo.setIndex(new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));
	return geo;
}

/** Create a VERTICAL unit billboard InstancedBufferGeometry — a quad in the XZ plane (positions
 *  (±1, 0, ±1)), i.e. world up = +z = the tree's vertical. UVs map local +x → u and local +z
 *  (up) → v, so the atlas cell's bottom (the tree base in the pitched bake) lands at the quad's
 *  bottom. ⚠ The v axis is INVERTED vs a naive mapping: the pitched bake puts the CROWN at the
 *  cell TOP in buffer space, and `bakeToTexture` (DataTexture, flipY=false) uploads buffer row 0
 *  (crown) to texture v = 0 — GL's BOTTOM when sampled. So the quad's bottom (world z = −1) must
 *  sample v = 1 (ground), and its top (z = +1) must sample v = 0 (crown). Set15 P4 §3.8 — the 3D
 *  cylindrical billboard (rotates about z only; trunks stay vertical). */
function billboardGeometry3D(): THREE.InstancedBufferGeometry {
	const geo = new THREE.InstancedBufferGeometry();
	geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array([-1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1]), 3));
	geo.setAttribute("uv", new THREE.BufferAttribute(new Float32Array([0, 1, 1, 1, 1, 0, 0, 0]), 2));
	geo.setIndex(new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));
	return geo;
}

/** The terrain height for a scenery instance's base (Set15 P4 §3.8). Samples the quadtree's
 *  D-LESS displacement lattice (the field the terrain geometry actually displaces by — using
 *  `heights` would float/bury trees by one ambient-detail band). Returns the sampled height, or
 *  the coarse fallback (0 = flat, matching unevaluated flat terrain) when no chunk covers the
 *  point yet.
 *
 *  2026-09-01 (scenery Option B) — uses the STABLE sampler (`sampleGeoHeightGeomAt_stable`):
 *  it reads a dirty-but-populated chunk's OLD lattice during a re-solve / viewpoint switch, so
 *  scenery keeps sitting on the previous solved surface instead of dropping to the flat fallback
 *  (0) and falling out of the 3D viewport.  The old `sampleGeoHeightGeomAt` returned null for
 *  every chunk after `markAllDirty` (dirty_data=true) → scenery dropped to 0 each re-solve. */
function sampleTerrainHeightForScenery(x: number, y: number): number {
	const h = terrainQuadtree_current?.sampleGeoHeightGeomAt_stable(x, y);
	return typeof h == "number" && Number.isFinite(h) ? h : 0;
}

export const SceneryRenderer = observer_mgl_r3((props: {mapID: string; view3DSettings?: MapSettings_View3D|null})=>{
	const {mapID, view3DSettings} = props;
	const {scene, camera, size, gl, invalidate} = useThree();

	const stateRef = useRef<Map<string, KindRenderState>>(new Map());
	const regionsRef = useRef<SceneryRegion[]>([]);
	const regionsSignatureRef = useRef("");
	// The GLOBAL budget enforcement point. The tracker persists for the component's lifetime so
	// its ATLAS-BYTE reservation survives across frames; its `config` is reassigned from the
	// authored `Mod_TerrainSceneries.budget` on each reactive render (so budget edits take effect),
	// and `beginFrame()` re-opens the per-frame instance allowance at the top of each frame.
	const budgetTrackerRef = useRef<SceneryBudgetTracker>(new SceneryBudgetTracker());
	const bakerRef = useRef<ImpostorBaker | null>(null);
	// The reactively-resolved kinds per active kindID. Computed in the RENDER BODY (the observer)
	// — NOT inside useFrame, which has no reactivity — so a kind edit re-runs the body, replaces
	// this map, and useFrame (re-registered each render) consumes the fresh values.
	const resolvedKindsRef = useRef<Map<string, ResolvedSceneryKind>>(new Map());
	// True while there are pending amortised TILE REBUILDS (a kind/region edit invalidates every
	// visible tile, but they rebuild only `tileBuildsPerFrame` per frame). The render body sets it
	// on a placement-signature change; useFrame clears it once no kind reports a pending build, and
	// calls `invalidate()` each frame while it's set — so on-demand (frameloop="demand") mode keeps
	// rendering until the regenerated tiles are all built.
	const needsRebuildRef = useRef(false);
	// Option A — the adaptive placement-chunk measurement. `lastFrameEndRef` = the timestamp when
	// this frame's placement finished; the gap between it and the next frame's start ≈ the previous
	// render() duration (in demand mode there's no idle between frames — invalidate() re-schedules
	// immediately), so it's the adaptive signal that sizes how much placement to do per redraw.
	const lastFrameEndRef = useRef<number>(performance.now());
	// Set17 — the SHARED per-tile placement-union cache (single-pass-per-tile across ALL kinds, so
	// the arbiter + margin run once per tile, not once per kind). Lives on the component (not the
	// per-kind render state) because placement is now kind-agnostic.
	const tileUnionCacheRef = useRef<Map<string, SceneryTileUnion>>(new Map());
	// The budget's PLACEMENT signature (the fields `placeSceneryTile` bakes into the cached tile
	// output — rank-thinning threshold + per-tile cap). A budget edit changes the cached placement,
	// so when it changes we must invalidate the whole tile-union cache + flag a rebuild (else the
	// old budget-thinned tiles keep being served until a page reload empties the cache). Other
	// budget fields (maxLiveTiles, maxAtlasBytes, placementRenderRatio, near-tier knobs) don't
	// affect placement output and are excluded by `placementSignature()`.
	const budgetSigRef = useRef<string>("");

	// Collect the map objects (mobx-observer → re-renders on change).

	// Collect the map objects (mobx-observer → re-renders on change).
	const mapObjects = GetMapObjects(mapID);
	// The packaged terrain objects (world-space path meshes + resolved closed flags) — the same
	// resolution TerrainRenderer uses, so the scenery-region bridge reads the SAME geometry the
	// terrain does (Set13 §3.4: one shared whitelist).
	const terrainObjects = ResolveMapObjectsForTerrain(mapObjects, /*resolvePathOriginPathIDs:*/ true, /*keepPathMeshLocalPoints:*/ false, /*calcPathMeshWorldPoints:*/ true);

	// Shading config + light rig for the bake (read each render; the mobx observer re-runs this
	// body when the mods change).
	const terrainMods = terrainModsOf(mapObjects);
	const firstMod = terrainMods[0];
	const resolvedShadingCfg = firstMod?.heightShadingConfig ?? new TerrainHeightShadingConfig();
	const lightDir = computeLightDirJS(resolvedShadingCfg.light_azimuth, resolvedShadingCfg.light_altitude);
	const lightRig: BakeLightRig = {
		azimuth: resolvedShadingCfg.light_azimuth,
		altitude: resolvedShadingCfg.light_altitude,
		ambient: resolvedShadingCfg.ambient,
		strength: resolvedShadingCfg.strength,
	};
	const terrainRegionAABBs = activeTerrainRegionAABBs(mapObjects, terrainMods);

	// Source the GLOBAL budget from the first enabled `Mod_TerrainSceneries` mod (the same union
	// the kind catalogue uses). The tracker REASSIGNS its config on each reactive render, so
	// budget edits take effect immediately; the tracker object itself persists (holding the
	// atlas-byte reservation across frames). Absent any mod → defaults.
	//
	// ⚠ A budget edit changes the PLACEMENT OUTPUT that `placeSceneryTile` baked into the cached
	// tile-union (`mppFadeStart`/`mppFadeEnd` → the rank-thinning threshold; `maxInstancesTotal` →
	// keepFrac; `maxInstancesPerTile` → the per-tile emission cap). The tile-cache key does NOT
	// include the budget, so we must explicitly invalidate + rebuild when the budget's placement
	// signature changes — otherwise a budget edit keeps serving the old budget-thinned tiles until
	// the page reloads and empties the cache (2026-09-01 fix; previously the user had to refresh).
	const sceneryMods = GetTerrainSceneriesMods(mapID);
	// NOTE: `sceneryMods[0]?.budget` may be a PLAIN deserialized object (not a `SceneryBudgetConfig`
	// class instance — the DB/mobx-sync restores it as a literal), so it has NO methods. Normalize
	// it through the class ctor (the §8 R1 convention: `new C(persisted)`) so the tracker gets a
	// real instance with methods. The PLACEMENT signature is read via the STATIC
	// `SceneryBudgetConfig.placementSignature` (which reads only plain fields, so it'd also work on
	// the un-normalized literal — the normalize here is only for the tracker's instance-methods).
	const nextBudget = new SceneryBudgetConfig(sceneryMods[0]?.budget ?? {});
	const nextBudgetSig = SceneryBudgetConfig.placementSignature(nextBudget);
	if (nextBudgetSig != budgetSigRef.current) {
		budgetSigRef.current = nextBudgetSig;
		tileUnionCacheRef.current.clear();
		needsRebuildRef.current = true; // budget changed → all cached tiles stale (rank-thinning + per-tile cap)
		debug(`render body: budget placement-signature changed → tile cache invalidated (sig=${nextBudgetSig})`);
	}
	budgetTrackerRef.current.config = nextBudget;

	// Resolve every active kind id REACTIVELY (this body is the observer — see the note on
	// resolvedKindsRef). Unknown kind ids are skipped (their region is dropped); membership is
	// checked against the catalogue so no try/catch is needed for the common "bad id" case.
	// Set16 §6: the union is over EVERY kindID in every region's `kinds` (a kind active as a
	// minority share of a mixed region still resolves + renders its own pass).
	const kindCatalogue = GetProcSceneryKindsByID(mapID);

	// Re-collect scenery regions when the path features change (cheap signature). The signature
	// MUST cover every field the placer reads from a region — the kinds+weights drive existence
	// + the mix, mode/strokeWidth/feather drive the geometry factor, spacing/presence overrides
	// drive density, and seedOverride/kindSeedOverride re-seed this region's RNG streams (a seed
	// edit MUST invalidate the tile-union cache, or the re-rolled placement would never appear). A change
	// to any of them must re-run collectSceneryRegions (and thus regenerate instances).
	// `collectSceneryRegions` is given the catalogue so unresolvable kind ids are dropped at
	// collect time (Set16 §1).
	//
	// The PATH GEOMETRY is part of the signature too: `collectSceneryRegions` reads
	// `_meshSet_world` (the polylines) + `_curve_closed_resolved` and bakes them into the region
	// bbox + `geoFactorAt` ramp, so editing an ObjPath's shape (points/transforms — which re-resolve
	// `terrainObjects` into a new `_meshSet_world`) MUST re-collect the regions (and regenerate
	// instances). Mirroring the material system's `materialRegionsSignature` (which includes
	// `polylines` + `closed`); absent the geometry here, a shape edit left the signature unchanged
	// and the distribution went stale (Set13 invalidation bug).
	const sfSig = terrainObjects.map(o=>(o.paths ?? []).map(p=>{
		const sf = p.sceneryFeature;
		const kindsPart = (sf?.kinds ?? []).map(k=>`${k.kindID}:${k.weight ?? 1}`).join(";");
		// World-space polylines (full float precision, deterministic per resolution) + the resolved
		// closed flag — the region geometry the placer's density/bbox derive from.
		const geoPart = `${(p._meshSet_world ?? []).map(poly=>poly.map(pt=>`${pt.x},${pt.y}`).join(";")).join("|")}:${p._curve_closed_resolved ? "c" : ""}`;
		return `${p.path_id ?? ""}:${kindsPart}:${sf?.disabled ? "d" : ""}:${sf?.mode ?? ""}:${sf?.strokeWidth ?? ""}:${sf?.feather ?? ""}:${sf?.spacingOverride ?? ""}:${sf?.presenceOverride ?? ""}:${sf?.dominance ?? ""}:${sf?.suppressPriorScenery ?? ""}:${sf?.seedOverride ?? ""}:${sf?.kindSeedOverride ?? ""}:${geoPart}`;
	}).join(",")).join("|");
	if (sfSig !== regionsSignatureRef.current) {
		regionsSignatureRef.current = sfSig;
		regionsRef.current = collectSceneryRegions(terrainObjects, new Set(kindCatalogue.keys()));
		needsRebuildRef.current = true; // region geometry/density changed → all tiles stale
		debug(`render body: regions re-collected → count=${regionsRef.current.length} sfSig=${sfSig}`);
	}

	const resolvedKinds = new Map<string, ResolvedSceneryKind>();
	for (const r of regionsRef.current) {
		for (const k of r.kinds ?? []) {
			const kindID = k.kindID;
			if (!kindID || !kindCatalogue.has(kindID) || resolvedKinds.has(kindID)) continue;
			resolvedKinds.set(kindID, ResolveSceneryKind(kindID, mapID));
		}
	}
	// A kind-default edit re-resolves to a DIFFERENT resolved kind (spacing/presence/etc changed) →
	// the tile cache key (which includes the placement signature) must be invalidated. Compare the
	// old resolved map (by identity) to detect a change, and flag a rebuild so useFrame keeps
	// rendering until the tiles rebuild.
	const resolvedChanged = resolvedKindsRef.current !== resolvedKinds ||
		[...resolvedKinds].some(([id, k])=>resolvedKindsRef.current.get(id) !== k) ||
		[...resolvedKindsRef.current.keys()].some(id=>!resolvedKinds.has(id));
	// Only log when the set of resolved kinds CHANGED (avoid spamming every render).
	if (resolvedChanged) {
		debug(`render body: resolvedKinds=${[...resolvedKinds.entries()].map(([id, r])=>`${id}(sp=${r.spacing},pres=${r.presence})`).join(", ")}`);
		needsRebuildRef.current = true; // kind placement defaults changed → all tiles stale
	}
	resolvedKindsRef.current = resolvedKinds;

	// Set14 P4 §4.9 — PRELOAD the active kinds' texture sets (the bake's MeshStandardMaterial
	// reads them; the bake signature includes their readiness/version token §4.10, so a bake taken
	// before they arrive is replaced, never cached). Kicked off each render body (idempotent);
	// the readiness state is not itself reactive, so the bake gate below re-checks it.
	useEffect(()=>{
		const barkTypes: string[] = [];
		const leafTypes: string[] = [];
		for (const k of resolvedKinds.values()) {
			const t = kindTextureTypeIDs(k);
			barkTypes.push(...t.barkTypes);
			leafTypes.push(...t.leafTypes);
		}
		if (barkTypes.length || leafTypes.length) void preloadTextures(barkTypes, leafTypes);
	}, [resolvedKindsRef.current]);

	// Create the baker once (renderer-side; needs the WebGL renderer).
	useEffect(()=>{
		bakerRef.current = new ImpostorBaker(gl, {cellSize: 256});
		return ()=>{
			// Dispose all render state meshes + textures (releases their atlas-byte reservations).
			for (const st of stateRef.current.values()) disposeKind(st, budgetTrackerRef.current);
			stateRef.current.clear();
			bakerRef.current = null;
		};
	}, [gl]);

	useFrame(()=>{
		// Option A — measure the previous render() duration. In demand mode invalidate() re-schedules
		// the next frame immediately after this frame's placement, so the gap since `lastFrameEndRef`
		// (the END of the previous frame's placement) ≈ the prior render() time — there's no idle
		// work in between. This is the adaptive signal that sizes how much placement to batch before
		// redrawing — a fast GPU (~ms) yields small batches (≈ old behaviour), SwiftShader (~1.3 s)
		// yields a whole render's worth of work per redraw.
		const lastRenderMs = Math.max(0, performance.now() - lastFrameEndRef.current);
		// The adaptive placement-chunk budget, HARD-CLAMPED to 3 s: beyond that the user could
		// mistake the map for a fully hung browser needing termination. On a fast GPU the ratio term
		// (≈ms) dominates and the clamp never binds. The `Math.max(1, ...)` FLOOR guarantees at
		// least ~1 ms of placement per frame, so a ratio of 0 (or a ~0 ms measured render) can never
		// deadlock the tile loop into placing nothing.
		const placementChunkMs = Math.max(1, Math.min(
			budgetTrackerRef.current.resolvePlacementRenderRatio() * lastRenderMs,
			3000,
		));

		// Re-open the per-frame instance allowance for this frame (§3.7 — per-frame instance count
		// is capped by a FIXED budget, never by world/region area or kind count).
		budgetTrackerRef.current.beginFrame();
		// Set15 P1 — the one camera seam (§3.5). Resolves BOTH camera types; the ortho path keeps
		// the old constant worldToScreen, and the perspective 3D camera gets a finite footprint.
		const viewInfo = resolveViewInfo(camera, size);
		// P2 — the scenery `mpp` drives rank thinning (§3.7): evaluating it at the tile's distance
		// gives distance-based thinning for free.  `placeSceneryInTile` takes a single `mpp` per
		// call; use the viewport-centre representative for the overall frame here (the per-tile
		// distance refinement is a P4 concern).
		const worldToScreen = viewInfo.worldToScreenAt(
			viewInfo.cameraView.x + viewInfo.cameraView.width / 2,
			viewInfo.cameraView.y + viewInfo.cameraView.height / 2,
		);
		const mpp = worldToScreen > 0 ? 1 / worldToScreen : 1;
		const isPerspective = viewInfo.isPerspective;

		// Set15 P4 (§3.8) — in 3D, bake at the active viewpoint's PITCH so the impostor atlas shows
		// the tree's true side view (not a top-down silhouette billboarded into the side view).
		// The pitch is part of the bake signature, so setting it here re-bakes on a pitch edit.
		// 2D keeps the default top-down pitch. The baker is fixed per component lifetime (the
		// `<Canvas key={mode}>` remount recreates it on a 2D↔3D toggle), so no re-fetch here.
		let pitch = 90;
		if (isPerspective && bakerRef.current) {
			const vp3D = resolveActiveViewpoint(mapID, view3DSettings?.view3D_activeViewpointObjID ?? null);
			if (vp3D) pitch = vp3D.mod.pitch;
			bakerRef.current.setPitch(Number.isFinite(pitch) ? pitch : 90);
		}
		// The 3D scenery draw distance (validated through a default — §8 R1). 2D ignores it.
		const sceneryMaxDistance = isPerspective
			? resolveSceneryMaxDistance(view3DSettings?.view3D_sceneryMaxDistance)
			: 0;
		// The eye position (for per-tile distance mpp + facing + max-distance cull).
		const cameraPos = {x: camera.position.x, y: camera.position.y};
		// Per-point mpp (metres per pixel) — in 3D this grows with the point's distance from the
		// eye, so evaluating it at a TILE's centre gives correct distance-based rank thinning (§3.8).
		const mppAt = (x: number, y: number): number => {
			const w2s = viewInfo.worldToScreenAt(x, y);
			return w2s > 0 ? 1 / w2s : 1;
		};

		// Set15.5 scenery-invisible fix — the camera's horizontal forward (ground-plane) + the
		// horizontal half-FOV, for the nearest-first ring enumeration + frustum reject. three.js
		// camera space: forward = -Z (the camera looks down its -Z axis). We negate the Z column
		// of matrixWorld (camera world rotation) and drop z to get the ground-plane forward; a
		// straight-down camera yields a degenerate (0,0) forward, in which case the wedge test
		// falls back to a full circle (see the `cosWedge` fallback in renderKind).
		let cameraForward = {x: 0, y: 0};
		let cameraHFovHalfAngle = 0;
		if (isPerspective) {
			const camM = camera.matrixWorld;
			cameraForward = {
				x: -camM.elements[8],
				y: -camM.elements[9],
			};
			const fl = Math.hypot(cameraForward.x, cameraForward.y);
			if (fl > 1e-6) { cameraForward.x /= fl; cameraForward.y /= fl; }
			const pc = camera as THREE.PerspectiveCamera;
			const vfovHalfTan = Math.tan((pc.fov * Math.PI / 180) / 2);
			const aspect = size.height > 0 ? size.width / size.height : 1;
			cameraHFovHalfAngle = Math.atan(vfovHalfTan * aspect);
		}

		// Visible world rect (expanded so instances near the edge are included).
		// Ortho: the old margin-based rect. Persp: the (clamped) footprint directly.
		const margin = 0.2;
		const cv = viewInfo.cameraView;
		const vp = isPerspective
			? {minX: cv.x, minY: cv.y, maxX: cv.x + cv.width, maxY: cv.y + cv.height}
			: {
				minX: cv.x - cv.width * margin,
				minY: cv.y - cv.height * margin,
				maxX: cv.x + cv.width * (1 + margin),
				maxY: cv.y + cv.height * (1 + margin),
			};

		const regions = regionsRef.current;
		// Active kind ids (from the regions) drive the hide/dispose sweep below (Set16: every kind
		// in every region's `kinds`, including minority shares of mixed regions).
		const activeKindIDs = new Set<string>();
		for (const r of regions) for (const k of r.kinds ?? []) if (k.kindID) activeKindIDs.add(k.kindID);

		// The region signature gates the tile cache: when it changes, every cached tile is stale
		// (region density/geometry changed) and must be rebuilt. Pass it into renderKind so the
		// tile-cache key includes it.
		const regionsSignature = regionsSignatureRef.current;

		// Render each RESOLVED kind from the reactively-rebuilt map (resolution lives in the render
		// body so a kind edit re-resolves via the observer; this frame just consumes the fresh map).
		// If any kind still has pending (amortised) tile builds this frame, keep requesting frames
		// so on-demand mode doesn't stop before the regenerated tiles are all built.
		let stillBuilding = false;
		const placementStats: SceneryPlacementStats = {placementMs: 0, tilesPlaced: 0};
		for (const [kindID, resolved] of resolvedKindsRef.current) {
			if (renderKind(stateRef.current, bakerRef.current, budgetTrackerRef.current, scene, resolved, kindID, regions, resolvedKindsRef.current, regionsSignature, tileUnionCacheRef.current, vp, mpp, lightRig, lightDir, terrainRegionAABBs, isPerspective, cameraPos, cameraForward, cameraHFovHalfAngle, sceneryMaxDistance, mppAt, placementChunkMs, placementStats)) {
				stillBuilding = true;
			}
		}
		if (needsRebuildRef.current || stillBuilding) {
			needsRebuildRef.current = stillBuilding;
			Invalidate3JS(invalidate, "scenery.rebuild", stillBuilding ? "amortised tile rebuilds still pending" : "rebuild complete — final frame");
			debug(`useFrame: ${stillBuilding ? "rebuilding tiles — requesting another frame" : "rebuild complete — stopping"}`);
		}

		// Set15 P5 debug — push the live scenery render state to `window.lfSceneryDebug.refresh()`
		// (agent debugging aid; no production cost). Builds a small per-kind summary + hands over
		// the ACTUAL live objects (scene, camera, gl, per-kind render-state Map, budget tracker)
		// so the console can inspect instance matrices / materials / geometry directly.
		{
			// Aggregate texture readiness across the active kinds (the bake gate is per-kind, but
			// the summary wants one number for "are the textures even loaded yet?").
			let allTexturesReady = true;
			for (const resolved of resolvedKindsRef.current.values()) {
				const texIDs = kindTextureTypeIDs(resolved);
				if (!texturesReady(texIDs.barkTypes, texIDs.leafTypes)) { allTexturesReady = false; break; }
			}
			const kinds: import("./SceneryDebug.js").SceneryDebugKindState[] = [];
			for (const [kindID, st] of stateRef.current) {
				const bill = st.billboardMesh;
				kinds.push({
					kindID,
					isPerspective: st.isPerspective,
					hasBillboard: bill != null,
					hasNearMeshes: (st.nearMeshes?.length ?? 0) > 0,
					billboardCount: bill ? bill.count : 0,
					nearCount: st.nearMeshes ? st.nearMeshes.reduce((a, nv)=>a + (nv.bark.count ?? 0), 0) : 0,
					treeCount: st.treeMesh ? st.treeMesh.count : 0,
					shadowCount: st.shadowMesh ? st.shadowMesh.count : 0,
					tileCount: tileUnionCacheRef.current.size,
					atlasWidth: st.atlasTexture?.image?.width ?? 0,
					atlasHeight: st.atlasTexture?.image?.height ?? 0,
					bakeSignature: st.bakeSignature,
				});
			}
			sceneryDebugUpdate({
				isPerspective,
				eye: {x: cameraPos.x, y: cameraPos.y, z: camera.position.z},
				sceneryMaxDistance,
				mpp,
				vp,
				tileSize: tileSizeForMpp(mpp),
				regionCount: regions.length,
				activeKindIDs: [...activeKindIDs],
				kinds,
				bakePitch: pitch,
				texturesReady: allTexturesReady,
				needsRebuild: needsRebuildRef.current,
				lastRenderMs,
				placementChunkMs,
				tilesPlacedThisFrame: placementStats.tilesPlaced,
				placementMsThisFrame: placementStats.placementMs,
				scene,
				camera,
				gl,
				renderStateMap: stateRef.current,
				budgetTracker: budgetTrackerRef.current,
			});
		}

		// Set15.5 — push the aggregated instanced/billboarded tree counts for the Stats panel. The
		// billboarded total is the 3D cylindrical-billboard count (persp) or the 2D flat-tree-quad
		// count (ortho, where every tree is a billboard); the instanced total is the near-tier real
		// meshes (persp only — 0 in 2D).
		{
			let instanced = 0, billboarded = 0;
			for (const st of stateRef.current.values()) {
				// Near-tier real meshes (per-variant bark+leaf InstancedMesh).
				if (st.nearMeshes) for (const nv of st.nearMeshes) {
					instanced += nv.bark.count ?? 0;
					instanced += nv.leaf.count ?? 0;
				}
				// The billboard: in 3D `billboardMesh` is the far tier; in 2D it's `treeMesh` (the
				// flat quad). Either way, this mesh holds the billboarded count.
				if (st.billboardMesh) billboarded += st.billboardMesh.count ?? 0;
				else billboarded += st.treeMesh ? st.treeMesh.count ?? 0 : 0;
			}
			RunInAction_Set(()=>{
				sceneryRenderStats.instancedTreeCount = instanced;
				sceneryRenderStats.billboardedTreeCount = billboarded;
				sceneryRenderStats.kindCount = stateRef.current.size;
				sceneryRenderStats.isPerspective = isPerspective;
			});
		}

		// Log the per-kind instance counts (what actually got drawn this frame).
		if (SCENERY_DEBUG && resolvedKindsRef.current.size) {
			debug(`useFrame: kinds=${resolvedKindsRef.current.size} mpp=${mpp.toFixed(3)} viewport=[${vp.minX.toFixed(0)},${vp.minY.toFixed(0)}→${vp.maxX.toFixed(0)},${vp.maxY.toFixed(0)}]` +
				` tiles=${stateRef.current.size} instances=` + [...stateRef.current.entries()].map(([id, st])=>`${id}:${st.treeMesh.count}`).join(","));
		}

		// Hide + dispose kinds no longer active.
		for (const [kindID, st] of stateRef.current) {
			const show = activeKindIDs.has(kindID);
			if (st.isPerspective && st.billboardMesh) {
				st.billboardMesh.visible = show;
				// Set15 P7 — the near-tier real meshes hide/show with the kind too.
				if (st.nearMeshes) for (const nv of st.nearMeshes) {
					nv.bark.visible = show;
					nv.leaf.visible = show;
				}
			} else {
				st.treeMesh.visible = show;
				st.shadowMesh.visible = show;
			}
		}

		// Option A — mark the END of this frame's placement. The gap from here to the next
		// `useFrame` entry (≈ the upcoming render() duration, since invalidate() re-schedules
		// immediately) is what sizes the NEXT frame's placement chunk.
		lastFrameEndRef.current = performance.now();
	});

	return null;
});

/** Dispose a kind's meshes + textures + remove from the scene. Releases the kind's atlas bytes
 *  back to the global budget tracker (§3.7 — a disposed bake frees its byte reservation). */
function disposeKind(st: KindRenderState, tracker: SceneryBudgetTracker): void {
	st.treeMesh.geometry.dispose();
	st.shadowMesh.geometry.dispose();
	(st.treeMesh.material as THREE.Material).dispose();
	(st.shadowMesh.material as THREE.Material).dispose();
	st.atlasTexture.dispose();
	if (st.treeMesh.parent) st.treeMesh.parent.remove(st.treeMesh);
	if (st.shadowMesh.parent) st.shadowMesh.parent.remove(st.shadowMesh);
	if (st.billboardMesh && st.billboardMesh !== st.treeMesh) {
		st.billboardMesh.geometry.dispose();
		(st.billboardMesh.material as THREE.Material).dispose();
		if (st.billboardMesh.parent) st.billboardMesh.parent.remove(st.billboardMesh);
	}
	// Set15 P7 — dispose the near-tier real meshes (per-variant geometry + materials).
	if (st.nearMeshes) {
		for (const nv of st.nearMeshes) {
			nv.bark.geometry.dispose();
			nv.leaf.geometry.dispose();
			nv.materials.bark.dispose();
			nv.materials.leaf.dispose();
			if (nv.bark.parent) nv.bark.parent.remove(nv.bark);
			if (nv.leaf.parent) nv.leaf.parent.remove(nv.leaf);
		}
	}
	if (st.atlasBytes > 0) tracker.releaseAtlas(st.atlasBytes);
}

/** Option A diagnostics — accumulated per frame and pushed to the `lfSceneryDebug` hook. */
export type SceneryPlacementStats = {
	/** Total wall-clock (ms) spent placing tiles this frame (across all kinds — the cache means
	 *  each tile's placement is attributed to the first kind that walks it). */
	placementMs: number;
	/** How many tiles were freshly placed (cache-miss builds) this frame. */
	tilesPlaced: number;
};

/** Ensure a kind's bake + meshes exist, then fill its instance buffers from the tiles overlapping
 *  the viewport. Cached by bake signature; re-bakes only on a signature change (§3.5).
 *  ALL budget enforcement flows through the shared {@link SceneryBudgetTracker} — the global
 *  per-frame instance allowance (shared across kinds) + the persistent atlas-byte budget + the
 *  per-frame tile-build amortisation. Exceeding a cap DEGRADES (fewer instances / skipped bake /
 *  deferred tile build); it never allocates past cap and never throws (§3.7). */
function renderKind(
	stateMap: Map<string, KindRenderState>,
	baker: ImpostorBaker | null,
	tracker: SceneryBudgetTracker,
	scene: THREE.Scene,
	resolved: ResolvedSceneryKind,
	kindID: string,
	regions: SceneryRegion[],
	/** Set16/Set17 — the resolved kinds map for the WHOLE union (all kinds in all regions' `kinds`).
	 *  The placer's `resolveKind`/`resolveVariants` closures read from it. */
	resolvedKinds: ReadonlyMap<string, ResolvedSceneryKind>,
	regionsSignature: string,
	/** Set17 — the SHARED per-tile placement-union cache (single-pass-per-tile across ALL kinds,
	 *  so the arbiter + margin run once per tile, not once per kind). */
	tileUnionCache: Map<string, SceneryTileUnion>,
	vp: {minX: number; minY: number; maxX: number; maxY: number},
	mpp: number,
	lightRig: BakeLightRig,
	lightDir: [number, number, number],
	terrainRegionAABBs: {minX: number; minY: number; maxX: number; maxY: number}[],
	isPerspective: boolean,
	cameraPos: {x: number; y: number},
	/** The camera's horizontal forward direction (ground-plane, normalised) — 3D only. Drives the
	 *  nearest-first ring enumeration + the frustum reject (tiles behind the camera are skipped). */
	cameraForward: {x: number; y: number},
	/** The camera's horizontal half-FOV (radians) — 3D only. A tile whose centre is outside this
	 *  half-angle from the view axis is behind/off the frustum and skipped. */
	cameraHFovHalfAngle: number,
	sceneryMaxDistance: number,
	mppAt: (x: number, y: number)=>number,
	/** Option A — the adaptive placement-chunk budget (ms): place tiles until this much cumulative
	 *  placement time is spent, THEN invalidate for a redraw. Scaled from the previous render's
	 *  duration × `placementRenderRatio`, clamped to 3 s in the caller. */
	placementChunkMs: number,
	/** Option A diagnostics — accumulated across kinds so the caller can report the frame total. */
	stats: SceneryPlacementStats,
): boolean {
	return timeIt("renderKind", ()=>renderKind_inner(
		stateMap, baker, tracker, scene, resolved, kindID, regions, resolvedKinds, regionsSignature,
		tileUnionCache, vp, mpp, lightRig, lightDir, terrainRegionAABBs, isPerspective, cameraPos,
		cameraForward, cameraHFovHalfAngle, sceneryMaxDistance, mppAt, placementChunkMs, stats,
	));
}

/** The un-timed body of {@link renderKind} (wrapped above for timing). */
function renderKind_inner(
	stateMap: Map<string, KindRenderState>,
	baker: ImpostorBaker | null,
	tracker: SceneryBudgetTracker,
	scene: THREE.Scene,
	resolved: ResolvedSceneryKind,
	kindID: string,
	regions: SceneryRegion[],
	/** Set16/Set17 — the resolved kinds map for the WHOLE union (all kinds in all regions' `kinds`).
	 *  The placer's `resolveKind`/`resolveVariants` closures read from it. */
	resolvedKinds: ReadonlyMap<string, ResolvedSceneryKind>,
	regionsSignature: string,
	/** Set17 — the SHARED per-tile placement-union cache (single-pass-per-tile across ALL kinds,
	 *  so the arbiter + margin run once per tile, not once per kind). */
	tileUnionCache: Map<string, SceneryTileUnion>,
	vp: {minX: number; minY: number; maxX: number; maxY: number},
	mpp: number,
	lightRig: BakeLightRig,
	lightDir: [number, number, number],
	terrainRegionAABBs: {minX: number; minY: number; maxX: number; maxY: number}[],
	isPerspective: boolean,
	cameraPos: {x: number; y: number},
	/** The camera's horizontal forward direction (ground-plane, normalised) — 3D only. Drives the
	 *  nearest-first ring enumeration + the frustum reject (tiles behind the camera are skipped). */
	cameraForward: {x: number; y: number},
	/** The camera's horizontal half-FOV (radians) — 3D only. A tile whose centre is outside this
	 *  half-angle from the view axis is behind/off the frustum and skipped. */
	cameraHFovHalfAngle: number,
	sceneryMaxDistance: number,
	mppAt: (x: number, y: number)=>number,
	/** Option A — the adaptive placement-chunk budget (ms): place tiles until this much cumulative
	 *  placement time is spent, THEN invalidate for a redraw. Scaled from the previous render's
	 *  duration × `placementRenderRatio`, clamped to 3 s in the caller. */
	placementChunkMs: number,
	/** Option A diagnostics — accumulated across kinds so the caller can report the frame total. */
	stats: SceneryPlacementStats,
): boolean {
	const signature = sceneryBakeSignature(resolved, lightRig);
	let st = stateMap.get(kindID);
	if (!st || st.bakeSignature !== signature) {
		if (st) { disposeKind(st, tracker); stateMap.delete(kindID); }
		if (!baker) return false;
		// Set14 P4 §4.9 — GATE the bake on texture readiness: the bake's MeshStandardMaterial reads
		// the texture cache, and a bake taken before the textures arrived would be flat + untextured.
		// Because the texture-readiness/version token (§4.10) joins the signature, a re-bake once
		// ready replaces it — but gating here avoids the transient untextured bake entirely (and the
		// preload above + this gate together ensure the first cached bake is textured). Deferring the
		// kind this frame is fine — the preload resolves async and the renderer re-renders.
		const texIDs = kindTextureTypeIDs(resolved);
		if (!texturesReady(texIDs.barkTypes, texIDs.leafTypes)) {
			debug(`renderKind(${kindID}): textures not ready — kind deferred (preload in flight)`);
			return false;
		}
		// Set15 P4 (§3.8) — bake at the locked viewpoint's PITCH (the renderer sets it on the baker
		// before this frame; 2D defaults to top-down). A pitch change is part of the atlas signature,
		// so entering 3D (or editing the viewpoint pitch) re-bakes the atlas.
		// Set15 P7 — use `bakeKindDetailed` so we ALSO get the generated variant meshes, which the
		// 3D near tier builds its real InstancedMeshes from (the impostor and the near mesh share
		// the same generated geometry).
		// Atlas-bytes guard BEFORE allocating the bake (§3.7): if the bake would push the global
		// atlas footprint past `maxAtlasBytes`, skip the kind entirely (degrade) rather than
		// allocating past cap. The estimate is exact for this kind's grid; the tracker persists
		// the reservation across frames until the kind is disposed.
		const detailed = timeIt("bakeKindDetailed", ()=>baker.bakeKindDetailed(resolved, lightRig));
		if (!detailed.bake) {
			debug(`renderKind(${kindID}): bake FAILED (unknown generator / empty atlas) — kind dropped`);
			return false;
		}
		const atlasBytes = estimateAtlasBytes(detailed.bake.cols, detailed.bake.rows, detailed.bake.cellSize);
		if (!tracker.tryReserveAtlas(atlasBytes)) {
			debug(`renderKind(${kindID}): atlas-bytes budget EXHAUSTED (${atlasBytes} bytes) — kind skipped`);
			return false;
		}
		st = createKindRenderState(resolved, detailed.bake, isPerspective, detailed.meshes, detailed.variants);
		st.atlasBytes = atlasBytes;
		st.bakeSignature = signature;
		// Add the meshes to the default scene (the R3F render loop draws them). In 3D the billboard
		// mesh is stored as BOTH treeMesh+shadowMesh (so the shared fill/dispose paths work) — add
		// it to the scene ONCE. In 2D add the tree + shadow pair.
		if (st.isPerspective) {
			scene.add(st.billboardMesh!);
			// Set15 P7 — add the near-tier real meshes (only the perspective/3D path has them).
			if (st.nearMeshes) for (const nv of st.nearMeshes) {
				scene.add(nv.bark);
				scene.add(nv.leaf);
			}
		} else {
			scene.add(st.treeMesh);
			scene.add(st.shadowMesh);
		}
		stateMap.set(kindID, st);
		debug(`renderKind(${kindID}): bake created (signature=${signature}, spacing=${resolved.spacing}, presence=${resolved.presence}, is3D=${isPerspective})`);
	}

	// Set the terrain-region clip + (2D) shadow light uniforms each frame (they may have changed).
	setKindFrameUniforms(st, terrainRegionAABBs, lightDir);

	// Tiles overlapping the viewport (Dial 2 — zoom-bucketed size, placement-invariant §3.3).
	// Set15 P4 — in 3D the tile size + the mpp passed to the placer are computed AT THE TILE'S
	// DISTANCE from the eye (mpp ≈ d·2tan(θ/2)/H grows with distance), so far tiles thin out via
	// rank thinning for FREE, and tiles beyond `sceneryMaxDistance` place nothing.
	// Set15.5 — the enumeration itself now radiates outward from the eye (nearest-first) with a
	// per-ring tile size + a frustum reject; see `enumerateSceneryTiles`.
	const maxLiveTiles = tracker.resolveMaxLiveTiles();

	// Cap the enumerated set at the LRU cache cap so No tile beyond what fits in the live set is
	// enumerated. Without this, `evictTileCache` trims the cache to `maxLiveTiles` each frame while
	// the enumeration may return up to its own 1500-tile budget → the excess tiles re-miss every
	// frame → `moreTilesPending` stays true → `invalidate()` is called forever and on-demand
	// (frameloop="demand") never idles. Nearest-first ordering means the farthest tiles are the
	// ones dropped (degrade, not a deadlock).
	const tileKeys = enumerateSceneryTiles({
		isPerspective, sceneryMaxDistance, vp, mpp,
		cameraPos, cameraForward, cameraHFovHalfAngle, mppAt,
		maxTiles: maxLiveTiles,
	});

	const instanceList: SceneryInstance[] = [];
	let moreTilesPending = false;
	const now = performance.now();
	// Option A — adaptive placement chunk. `placeSceneryTile` is pure/deterministic CPU, so we batch
	// tiles until `placementChunkMs` of accumulated placement time is spent (measured around each
	// `placeSceneryTile` call), THEN invalidate for one redraw. On a slow software renderer this
	// coalesces a whole render's worth of work into a single frame; on a fast GPU the chunk is ~ms so
	// the per-frame batch stays small (≈ the old `tileBuildsPerFrame` behaviour). The caller already
	// clamped the chunk to 3 s. `placementChunkMs` is passed in (per kind), so each kind shares the
	// SAME budget — but placement is cached per-tile across kinds, so the first kind to walk a tile
	// pays for it and later kinds hit the cache. We track cumulative time (not a hard tile count) so
	// expensive tiles don't get starved out of a frame by many cheap ones.
	const placementStart = performance.now();
	// The measured placement time is attributed to the FIRST kind that does the tile's placement
	// (the cache means only one kind ever pays per tile). This is correct: the renderer's overall
	// placement progress per redraw is what the ratio sizes, and the cache makes cross-kind duplicate
	// work ~free.
	let placementMs = 0;
	// Set17 — the resolved-kind signature (a kind-default edit must invalidate the union cache).
	const kindUnionSig = resolvedKindsSignature(resolvedKinds);
	// The tile-union cache key = `<regionsSigHash>:<kindUnionSigHash>:tileCoords[+persp mpp]`.
	// Both signatures are CONSTANT across every tile in this renderKind call (and every kind), so
	// we build the `<regionsSigHash>:<kindUnionSigHash>:` PREFIX once. We embed the 32-bit HASH of
	// each signature (not the full kilobyte string): V8 re-hashes the whole key string on every
	// `Map.get`, so the pre-hash key made each of the ~1080 lookups/frame hash ~1 KB of polylines —
	// `stmt.cacheGet` was 94% of the tileLoop (the real zoom-out hotspot my earlier batch-grant +
	// prefix-hoist did NOT fix). A short hash token keeps invalidation (hash changes iff signature
	// content changes) while making `Map.get` hash ~30 bytes. The astronomically-rare 32-bit
	// collision only risks a stale cached tile, which the LRU evicts + the signature gate rebuilds.
	const tileKeyPrefix = `${hashString32(regionsSignature).toString(16)}:${hashString32(kindUnionSig).toString(16)}:`;
	timeIt("renderKind.tileLoop", ()=>{
	for (const tk of tileKeys) {
		// Global per-frame instance allowance — shared across ALL kinds (the plan's invariant:
		// peak instance count is a function of a fixed budget, never of kind count/area).
		if (tracker.instancesRemaining <= 0) break;
		// Set17 — the tile key is REGION-signature + resolved-KIND-signature scoped (NOT per-kind,
		// and NOT the Set16 shared-layout signature — the shared lattice is gone; each region places
		// independently). A change to any region feature OR any resolved kind default invalidates
		// the whole tile-union cache (correct; the plan's per-tile region-overlap key is future
		// work — §5.2 — and would narrow rebuilds, but the CONTENT outside an edited region's
		// footprint is already byte-identical, so the rebuild is wasted-but-correct work).
		// In 3D the mpp at the tile's distance also affects rank-thinning EMISSION, so it must be
		// part of the key (a far tile at a changed distance/bucket re-emits). NOTE: the canonical
		// layout is mpp-independent (D6 rank after arbitration), so the mpp dimension only affects
		// which subset is emitted — a cheaper cache with an mpp-scaled filter is future work.
		// 2026-08-28 — the loop body is split into per-statement `timeStmt` buckets (see
		// SceneryTiming) so a profiler trace that folds this closure's self-time into one node can
		// still reveal WHICH statement dominates via `window.lfSceneryTiming.dump()` (`stmt.*`).
		const tileMpp = isPerspective ? mppAt(tk.minX + tk.size / 2, tk.minY + tk.size / 2) : mpp;
		const key = `${tileKeyPrefix}${tk.minX},${tk.minY},${tk.size}${isPerspective ? `:m${tileMpp.toFixed(4)}` : ""}`;
		let entry = timeStmt("cacheGet", ()=>tileUnionCache.get(key));
		if (!entry) {
			// Option A — adaptive chunk: keep placing until the placement-time budget is spent
			// (clamped to 3 s in the caller). Only check the budget BEFORE a cache-miss build — a
			// cached tile costs nothing and must still be pushed this frame. `placementMs` only
			// accrues on genuine (cache-miss) builds, and is measured around the pure `placeSceneryTile`
			// call so the budget tracks the work that would otherwise have to wait for the next redraw.
			if (placementMs >= placementChunkMs) { moreTilesPending = true; continue; } // amortise — redraw, build more next frame
			const result = timeStmt("placeTile", ()=>placeSceneryTile({
				regions,
				budget: tracker.config, minX: tk.minX, minY: tk.minY, tileSize: tk.size, mpp: tileMpp,
				// Set17 — the placer resolves each region's kinds independently (single-pass over
				// all regions). The pure placer stays DB-free; the renderer supplies the union.
				resolveKind: k=>resolvedKinds.get(k) ?? null,
				resolveVariants: k=>{ const r = resolvedKinds.get(k); return r ? deriveVariants(r) : null; },
			}));
			placementMs = performance.now() - placementStart;
			// NOTE: `placementMs` = total wall-clock elapsed since `placementStart` (ASSIGNED, not
			// accumulated) — a single subtraction per tile, monotonic as placement proceeds. It
			// slightly over-counts the per-frame fixed overhead (cache gets / other work), which is
			// immaterial vs the ms-scale budget it's compared against.
			stats.placementMs = placementMs;
			stats.tilesPlaced++;
			const instancesByKind = result.instancesByKind;
			debug(`renderKind(${kindID}): TILE BUILD minX=${tk.minX},minY=${tk.minY},size=${tk.size} → ${[...instancesByKind.entries()].map(([id, arr])=>`${id}:${arr.length}`).join(",")} instances`);
			entry = {instancesByKind, lastUsed: now};
			tileUnionCache.set(key, entry);
		}
		entry.lastUsed = now;
		// Batch-grant the instance allowance for this tile's whole instance list at once. The
		// 2026-08-28 zoom-out hotspot: per-instance `reserveInstances(1)` (a function call doing a
		// Math.min + subtract + Math.max) over a ~300 k cached-instance set, re-walked per kind ×
		// per tile, accumulated into this loop's inline (untimed) cost — the trace's ~17 s with no
		// children. `reserveInstances(len)` returns `min(len, remaining)`; pushing exactly that many
		// is SEMANTICALLY IDENTICAL to the old per-element loop — same shared-pool decrement, same
		// cross-kind fairness (each kind still draws from the one per-frame allowance, and an
		// exhausted pool grants 0 → the next tile's top-of-loop `instancesRemaining <= 0` break
		// fires exactly as before) — but collapses N function calls per tile into one.
		const tileInstances = timeStmt("instancesByKindGet", ()=>entry.instancesByKind.get(kindID));
		if (tileInstances && tileInstances.length > 0) {
			const grant = tracker.reserveInstances(tileInstances.length);
			timeStmt("pushLoop", ()=>{
				for (let i = 0; i < grant; i++) instanceList.push(tileInstances[i]);
			});
		}
	}
	timeStmt("evict", ()=>evictTileCache(tileUnionCache, maxLiveTiles));
	});

	// Set15 P7 — the near-tier knobs (validated through the tracker's config, resolved to
	// defaults — §8 R1). 2D ignores them (fillKindInstances only partitions when 3D).
	const nearMeshMaxDistance = isPerspective ? tracker.resolveNearMeshMaxDistance() : 0;
	const nearMeshMaxInstances = isPerspective ? tracker.resolveNearMeshMaxInstances() : 0;
	fillKindInstances(st, instanceList, lightDir, cameraPos, nearMeshMaxDistance, nearMeshMaxInstances);

	// True = this kind still has tiles that were deferred by the per-frame build throttle — the
	// caller should request another frame so they build. (Only meaningful during a rebuild.)
	return moreTilesPending;
}

/** Create the meshes for a kind's bake and add them to the default scene.
 *  Set15 P4 (§3.8) — in PERSPECTIVE mode this creates the 3D cylindrical-billboard mesh (the far
 *  tier) PLUS the Set15 P7 near-tier real meshes (no shadow pass, no depth key); in 2D it creates
 *  the tree+shadow pair unchanged. */
function createKindRenderState(
	resolved: ResolvedSceneryKind,
	bake: SceneryBakeResult,
	isPerspective: boolean,
	meshes: SceneryMesh[] = [],
	variants: SceneryVariant[] = [],
): KindRenderState {
	const atlasTexture = bakeToTexture(bake);

	// ── Set15 P4 (§3.8) — 3D cylindrical-billboard pass (perspective only). ──
	// A vertical InstancedMesh per kind, textured with the PITCH-baked atlas. The depth key is
	// GONE (the real depth buffer occludes correctly); the shadow pass is dropped (P8). The
	// material is OPAQUE (transparent false) + alpha-tested (the TREE_FRAGMENT_GLSL `discard`s
	// sub-0.5-alpha texels itself), so it renders in the opaque pass with correct depth ordering
	// against the displaced terrain and other trees. Same output-transfer chain as the 2D tree
	// quad (it draws to the default framebuffer).
	if (isPerspective) {
		const billMat = new THREE.ShaderMaterial({
			vertexShader: BILLBOARD3D_VERTEX_GLSL,
			fragmentShader: TREE_FRAGMENT_GLSL,
			uniforms: {
				u_atlas: {value: atlasTexture},
				u_atlasCols: {value: bake.cols},
				u_atlasRows: {value: bake.rows},
				u_opacity: {value: 1},
				u_texelX: {value: 1 / bake.atlasWidth},
				u_texelY: {value: 1 / bake.atlasHeight},
				u_terrainRegionCount: {value: 0},
				u_terrainRegions: {value: Array.from({length: TERRAIN_REGION_CLIP_MAX}, ()=>new THREE.Vector4(0, 0, 0, 0))},
			},
			// Opaque + alpha-test: the fragment itself `discard`s below alpha 0.5, so fragments
			// are either fully written (depth-correct) or absent. No transparent sorting needed.
			transparent: false,
			depthWrite: true,
			depthTest: true,
			blending: THREE.NormalBlending,
			alphaTest: 0.5,
		});
		billMat.customProgramCacheKey = ()=>`scenery-billboard3d:${bake.signature}`;
		const billGeo = billboardGeometry3D();
		const billMesh = new THREE.InstancedMesh(billGeo, billMat, 1);
		billMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
		billMesh.frustumCulled = false;
		// Per-instance attributes (variant cell select + tint).
		billGeo.setAttribute("instanceVariant", new THREE.InstancedBufferAttribute(new Float32Array(0), 1));
		billGeo.setAttribute("instanceTint", new THREE.InstancedBufferAttribute(new Float32Array(0), 1));

		// ── Set15 P7 (§3.8) — the NEAR tier: a real InstancedMesh per (kind, material-kind,
		// variant), built from the generated variant meshes the bake produced. Each variant's
		// bark + leaf geometry is shared (INDEXED) across all instances of that variant — at
		// ~13–20 k tris/tree and ~50–200 near instances, this is memory-affordable (one indexed
		// geometry per variant, ~6 variants × 2 parts) and reuses the exact geometry the atlas
		// was baked from (no impostor/near-mesh divergence). Per-instance tint via `instanceColor`
		// (rgb = tint³); the per-variant material.color carries the authored barkTint/leafTint.
		// Drawn under the 3D scene lights (`Map3D_SceneLights`) — the same light rig the bake
		// used, so the near mesh and the far impostor share a shading model (§4.5).
		const nearMeshes = buildNearTierMeshes(meshes, variants);

		return {
			resolved, bake, atlasTexture,
			treeMesh: billMesh, shadowMesh: billMesh,
			billboardMesh: billMesh, nearMeshes, isPerspective: true,
			bakeSignature: "", atlasBytes: 0,
			_matrix: new THREE.Matrix4(),
			_quat: new THREE.Quaternion(),
			_pos: new THREE.Vector3(),
			_scl: new THREE.Vector3(),
			_axis: new THREE.Vector3(0, 0, 1),
		};
	}

	// ── Tree pass material (alpha-test + depth-write on) ──
	// Depth ordering (§3.6 §4.7): the depth key = crown-top height is folded into the quad's z
	// (taller → higher z → closer to the top-down camera). three sorts TRANSPARENT objects
	// back-to-front (painter), so the taller (closer) tree draws LAST and, with depth-write ON +
	// the DEFAULT LessEqual depth func, overwrites the shorter crown it overlaps — no explicit
	// sorting, no terrain data. (GreaterEqual would be wrong here: the depth buffer clears to
	// the FAR value, so a near fragment's smaller depth fails `>= far` and the whole tree is
	// discarded.)
	// ⚠ Set14 P7 §4.11 REVISED — `toneMapped` MUST stay default-true (ShaderMaterial default).
	// This material's fragment runs the output-transfer chain (TREE_FRAGMENT_GLSL calls
	// `toneMapping()` + `linearToOutputTexel()`), which three only injects when the program's
	// toneMapping != NoToneMapping. Setting `toneMapped: false` here would compile the program
	// with NoToneMapping (no `#define TONE_MAPPING`, identity `linearToOutputTexel`) and the
	// transfer block would silently no-op — re-introducing the near-black leaves.
	const treeMat = new THREE.ShaderMaterial({
		vertexShader: TREE_VERTEX_GLSL,
		fragmentShader: TREE_FRAGMENT_GLSL,
		uniforms: {
			u_atlas: {value: atlasTexture},
			u_atlasCols: {value: bake.cols},
			u_atlasRows: {value: bake.rows},
			u_terrainZ: {value: SCENERY_TERRAIN_Z},
			u_depthScale: {value: TREE_DEPTH_SCALE},
			// Half-texel inset for the tree pass (see TREE_FRAGMENT_GLSL): keeps LinearFilter from
			// bleeding the neighbouring variant's texels at the cell boundary.
			u_texelX: {value: 1 / bake.atlasWidth},
			u_texelY: {value: 1 / bake.atlasHeight},
			u_opacity: {value: 1},
			u_terrainRegionCount: {value: 0},
			u_terrainRegions: {value: Array.from({length: TERRAIN_REGION_CLIP_MAX}, ()=>new THREE.Vector4(0, 0, 0, 0))},
		},
		transparent: true,
		depthWrite: true,
		depthTest: true,
		blending: THREE.NormalBlending,
		alphaTest: 0.5,
	});
	treeMat.customProgramCacheKey = ()=>`scenery-tree:${bake.signature}`;

	// ── Shadow pass material (multiply blend, depth-write off) ──
	const shadowMat = new THREE.ShaderMaterial({
		vertexShader: SHADOW_VERTEX_GLSL,
		fragmentShader: SHADOW_FRAGMENT_GLSL,
		uniforms: {
			u_terrainZ: {value: SCENERY_TERRAIN_Z},
			u_atlas: {value: atlasTexture},
			u_atlasCols: {value: bake.cols},
			u_atlasRows: {value: bake.rows},
			u_lightDir: {value: new THREE.Vector3(0.7071, 0, 0.7071)},
			u_texelX: {value: 1 / bake.atlasWidth},
			u_texelY: {value: 1 / bake.atlasHeight},
			// Core shadow colour + strength. The fragment emits mix(1, tint, shade) with
			// shade = crownAlpha × u_shadowStrength, so the CORE (full alpha) of the shadow is
			// mix(1, tint, strength). With Min-blend that value IS the final shadow colour, so it
			// must be genuinely dark — a high strength lets the core reach the (dark) tint.
			// (Old 0.45/0.5 → mix(1,0.45,0.5)=0.725, a light-gray — the "shadows are light-gray"
			// bug. tint 0.22/strength 1.0 → core 0.22, a proper dark soft shadow.)
			u_shadowStrength: {value: 1.0},
			u_shadowTint: {value: new THREE.Vector3(0.22, 0.22, 0.22)},
			u_terrainRegionCount: {value: 0},
			u_terrainRegions: {value: Array.from({length: TERRAIN_REGION_CLIP_MAX}, ()=>new THREE.Vector4(0, 0, 0, 0))},
		},
		transparent: true,
		depthWrite: false,
		depthTest: true,
		depthFunc: THREE.LessEqualDepth,
		// Set14 P7 §4.11 — the SHADOW pass is NOT on the output transfer chain. Its fragment
		// output is a BLEND OPERAND (`mix(vec3(1), tint, shade)` under Min-equation), not a
		// colour: tonemapping/sRGB-encoding an operand is a different operation and would break
		// the multiply/min semantics. So the shadow shader must NOT call `toneMapping()` /
		// `linearToOutputTexel()` (it doesn't), AND we mark it `toneMapped: false` so three uses
		// `NoToneMapping` for its program (no `#define TONE_MAPPING`) — belt-and-braces against a
		// future edit adding a transfer call, and keeps the renderer's tone-mapping setting from
		// affecting the shadow's blend value.
		toneMapped: false,
		// MIN blend — idempotent + order-independent (like multiply, but overlapping shadows
		// DON'T compound to black: min(x, x) == x). The fragment emits mix(1, tint, shade), so
		// min(dst, mix) leaves un-shadowed fragments unchanged and darkens shaded ones to the
		// tint colour. ⚠ MUST set `blending: CustomBlending` or three.js IGNORES the custom
		// equation/factors (WebGLState.setBlending resets to blendEquation(FUNC_ADD) whenever
		// blending !== CustomBlending). Without it, NormalBlending + the fragment's constant
		// alpha 1 → the huge shadow quad overwrites the terrain with mix(1, tint, shade) ≈ WHITE
		// — the "solid white background to the quads" bug.
		blending: THREE.CustomBlending,
		blendEquation: THREE.MinEquation,
		blendSrc: THREE.OneFactor,
		blendDst: THREE.OneFactor,
	});
	shadowMat.customProgramCacheKey = ()=>`scenery-shadow:${bake.signature}`;

	// Unit quad geometries (each pass needs its own attribute set, so separate geometries).
	const treeGeo = quadGeometry(true);
	const shadowGeo = quadGeometry(false);

	const treeMesh = new THREE.InstancedMesh(treeGeo, treeMat, 1);
	const shadowMesh = new THREE.InstancedMesh(shadowGeo, shadowMat, 1);
	// renderOrder drives transparent-object sorting: shadows before trees, both after terrain.
	treeMesh.renderOrder = TREE_RENDER_ORDER;
	shadowMesh.renderOrder = SHADOW_RENDER_ORDER;
	treeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
	shadowMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
	treeMesh.frustumCulled = false;
	shadowMesh.frustumCulled = false;

	// Per-instance attributes (created empty; filled by fillKindInstances). The TREE geometry
	// carries scale/variant/depthKey; the SHADOW geometry carries variant + shearLen (the shadow
	// is the crown disc, scaled by the same crownHalf the tree quad uses and shifted down-light
	// by the per-instance shear in the vertex shader).
	const scaleAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	const variantAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	const depthAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	const tintAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	treeGeo.setAttribute("instanceScale", scaleAttr);
	treeGeo.setAttribute("instanceVariant", variantAttr);
	treeGeo.setAttribute("instanceDepthKey", depthAttr);
	treeGeo.setAttribute("instanceTint", tintAttr);
	const shadowVariantAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	const shadowCrownHalfAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	const shadowShearAttr = new THREE.InstancedBufferAttribute(new Float32Array(0), 1);
	shadowGeo.setAttribute("instanceVariant", shadowVariantAttr);
	shadowGeo.setAttribute("instanceCrownHalf", shadowCrownHalfAttr);
	shadowGeo.setAttribute("instanceShearLen", shadowShearAttr);

	return {
		resolved, bake, atlasTexture, treeMesh, shadowMesh,
		billboardMesh: null, isPerspective: false,
		nearMeshes: null,
		bakeSignature: "", atlasBytes: 0,
		_matrix: new THREE.Matrix4(),
		_quat: new THREE.Quaternion(),
		_pos: new THREE.Vector3(),
		_scl: new THREE.Vector3(),
		_axis: new THREE.Vector3(0, 0, 1),
	};
}

/** Set15 P7 (§3.8) — build the NEAR-TIER real meshes for a kind from its generated variant meshes
 *  (the same meshes the bake atlas was rendered from). Returns one `NearTierVariantState` per
 *  variant, each holding a bark + a leaf `InstancedMesh` sharing that variant's INDEXED geometry
 *  and the same `buildSceneryMaterials` materials the bake used — so the near mesh and the far
 *  impostor differ only in resolution, never in shading model (§4.5).
 *
 *  WHY per-variant InstancedMeshes (not one merged per kind): three's `InstancedMesh` draws ONE
 *  geometry with ONE material. A merged kind mesh would need a per-instance range (a custom shader,
 *  abandoning `MeshStandardMaterial`), or `setGeometryAt` (non-indexed only → ~3× the vertex
 *  memory of the already-large ez-tree meshes). Per-variant shared INDEXED geometry is the memory-
 *  affordable middle: the ~13–20 k tris/tree × ~50–200 near instances render as a handful of draw
 *  calls (one bark + one leaf per variant) with zero per-frame geometry churn. */
function buildNearTierMeshes(meshes: SceneryMesh[], variants: SceneryVariant[]): NearTierVariantState[] | null {
	if (meshes.length == 0) return null;
	const near: NearTierVariantState[] = [];
	for (let i = 0; i < meshes.length; i++) {
		const mesh = meshes[i];
		const variant = variants[i];
		if (!variant) continue;
		// Find the bark + leaf parts of this variant's mesh (one each, per the Gen_EzTree parts
		// contract §4.7). A part missing its material kind degrades to skipping it (never throw).
		const barkPart = mesh.parts.find(p=>p.materialKind == "bark");
		const leafPart = mesh.parts.find(p=>p.materialKind == "leaf");
		const geoBark = barkPart ? partToGeometry(barkPart) : null;
		const geoLeaf = leafPart ? partToGeometry(leafPart) : null;
		if (!geoBark && !geoLeaf) continue;

		// The SAME materials the bake pre-lit with — built once per variant, shared by both the
		// bake (already disposed) and the near tier.
		const materials = buildSceneryMaterials(variant.params);

		// Capacity 1; `fillKindInstances` grows the matrices + instanceCount as instances arrive.
		const makeMesh = (geo: THREE.BufferGeometry | null, mat: THREE.Material): THREE.InstancedMesh | null => {
			if (!geo) return null;
			const im = new THREE.InstancedMesh(geo, mat, 1);
			im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
			im.count = 0;
			// ⚠ frustumCulled MUST stay false. three.js frustum-culls an InstancedMesh using the
			// geometry's OWN bounding sphere (centred near the geometry origin — here the tree
			// mesh, e.g. (0,0,7)), NOT the instance positions (which are scattered across the map
			// via instanceMatrix, potentially many km from origin). With culling on, the mesh is
			// culled whenever the geometry origin is outside the camera frustum — which is almost
			// always in a world-scale map — so near trees vanish. Same reason the 2D tree/shadow
			// and the 3D billboard all set frustumCulled = false.
			im.frustumCulled = false;
			// Set15 P8 — near-tier real meshes are set to cast/receive in `fillNearTierMeshes`,
			// GATED on whether a shadow light is actually active (read from the module-global
			// `terrainShadowLight_current`). We deliberately do NOT set them here unconditionally:
			// if `gl.shadowMap.enabled` ever leaks true (the Map3D_SceneLights leak this fixes),
			// a cast/receive mesh with NO shadow light would run the shadow-receive path for no
			// reason — a scenery-disappearing source (trees vanish even with shadows off).
			return im;
		};
		const bark = makeMesh(geoBark, materials.bark);
		const leaf = makeMesh(geoLeaf, materials.leaf);
		if (!bark && !leaf) continue;

		near.push({
			mesh, variantIndex: variant.index,
			bark: bark!, leaf: leaf!,
			geoBark: geoBark!, geoLeaf: geoLeaf!,
			materials,
		});
	}
	return near.length > 0 ? near : null;
}

/** The tree material as a ShaderMaterial (we always construct it as one). */
function treeMat(st: KindRenderState): THREE.ShaderMaterial {
	return st.treeMesh.material as THREE.ShaderMaterial;
}
/** The shadow material as a ShaderMaterial. */
function shadowMat(st: KindRenderState): THREE.ShaderMaterial {
	return st.shadowMesh.material as THREE.ShaderMaterial;
}

/** Set the per-frame uniforms shared by both passes: the terrain-region clip AABBs (§4.8) and
 *  the shadow pass's light direction + extent (which re-orients the teardrop with the light).
 *  Set15 P4 — in 3D (billboard) mode the shadow uniforms don't exist; only the region clip is
 *  applied (to the billboard material). */
function setKindFrameUniforms(
	st: KindRenderState,
	terrainRegionAABBs: {minX: number; minY: number; maxX: number; maxY: number}[],
	lightDir: [number, number, number],
): void {
	const clipArr = new Array<THREE.Vector4>(TERRAIN_REGION_CLIP_MAX);
	for (let i = 0; i < TERRAIN_REGION_CLIP_MAX; i++) clipArr[i] = new THREE.Vector4(0, 0, 0, 0);
	const count = Math.min(terrainRegionAABBs.length, TERRAIN_REGION_CLIP_MAX);
	for (let i = 0; i < TERRAIN_REGION_CLIP_MAX; i++) {
		const a = terrainRegionAABBs[i];
		if (i < count && a) clipArr[i].set(a.minX, a.minY, a.maxX, a.maxY);
		else clipArr[i].set(0, 0, 0, 0);
	}

	if (st.isPerspective && st.billboardMesh) {
		const bm = st.billboardMesh.material as THREE.ShaderMaterial;
		const bClip = bm.uniforms.u_terrainRegions.value as THREE.Vector4[];
		for (let i = 0; i < TERRAIN_REGION_CLIP_MAX; i++) bClip[i].copy(clipArr[i]);
		(bm.uniforms.u_terrainRegionCount.value as number) = count;
		return;
	}

	const tm = treeMat(st);
	const sm = shadowMat(st);
	const tClip = tm.uniforms.u_terrainRegions.value as THREE.Vector4[];
	for (let i = 0; i < TERRAIN_REGION_CLIP_MAX; i++) tClip[i].copy(clipArr[i]);
	(tm.uniforms.u_terrainRegionCount.value as number) = count;
	// Mirror the clip onto the shadow material.
	const sClipArr = sm.uniforms.u_terrainRegions.value as THREE.Vector4[];
	for (let i = 0; i < TERRAIN_REGION_CLIP_MAX; i++) sClipArr[i].copy(clipArr[i]);
	(sm.uniforms.u_terrainRegionCount.value as number) = count;

	// Shadow light direction + geometry. u_lightDir points TOWARD the sun (used by the terrain
	// hillshade / baked crowns), but shadows are cast AWAY from the sun — so the shadow material
	// receives the NEGATED direction. This keeps the SDF tail orientation consistent with the
	// down-light instance offset in `fillKindInstances` (both run the opposite way to the light).
	(sm.uniforms.u_lightDir.value as THREE.Vector3).set(-lightDir[0], -lightDir[1], -lightDir[2]);
}

/** Fill the instance buffers from a flat instance list and update the mesh(es)' matrices.
 *  Set15 P4 — 3D billboard mode fills the billboard mesh; 2D fills the tree+shadow pair.
 *  Set15 P7 — in 3D, instances within `nearMeshMaxDistance` of the eye fill the NEAR-TIER real
 *  meshes (per-variant bark + leaf InstancedMeshes, capped at `nearMeshMaxInstances` keeping the
 *  nearest); the rest fill the far-tier billboard.  Buffers are GROWN-AND-REUSED (not reallocated
 *  every frame): at the 300 k-instance cap the old per-frame realloc churned ~19 MB of typed
 *  arrays per frame. We only reallocate when the new count exceeds the current buffer capacity,
 *  and we set `count` to the exact n so the shader's per-instance attributes index only the live
 *  instances. */
function fillKindInstances(
	st: KindRenderState,
	instances: SceneryInstance[],
	lightDir: [number, number, number],
	cameraPos: {x: number; y: number},
	nearMeshMaxDistance: number,
	nearMeshMaxInstances: number,
): void {
	timeIt("fillKindInstances", ()=>{
		fillKindInstances_inner(st, instances, lightDir, cameraPos, nearMeshMaxDistance, nearMeshMaxInstances);
	});
}

/** The un-timed body of {@link fillKindInstances} (wrapped above for timing). */
function fillKindInstances_inner(
	st: KindRenderState,
	instances: SceneryInstance[],
	lightDir: [number, number, number],
	cameraPos: {x: number; y: number},
	nearMeshMaxDistance: number,
	nearMeshMaxInstances: number,
): void {
	const n = instances.length;

	// The quad/billboard is sized to the BAKE's framed world extent (`cellWorldHalfExtent`), NOT
	// an independently-authored footprintRadius — the old code sized by `footprintRadius * scale`,
	// which could disagree with the bake framing (editing crownRadius changed the baked cell but
	// not the quad size → trees drawn ~0.6× their baked size).
	// 2026-09-01 — `cellWorldHalfExtentZ` (the tree's projected vertical half-extent) is
	// available for a future non-square billboard; the current square quad uses X only.
	const quadHalfExtent = resolveBudgetNumber(st.bake.cellWorldHalfExtent, 1);
	const variantMaxZ = st.bake.variantMaxZ;
	const {_matrix: m, _quat: q, _pos: p, _scl: s, _axis: axis} = st;

	// ── Set15 P4/P7 (§3.8) — 3D fill (perspective only). ──
	if (st.isPerspective && st.billboardMesh) {
		// Set15 P7 — partition instances into NEAR (within `nearMeshMaxDistance` of the eye, up to
		// `nearMeshMaxInstances` keeping the nearest) and FAR (the billboard). With a locked
		// camera, "near" is a fixed set recomputed per frame cheaply. The near cap is per-kind and
		// validated through a default (§8 R1). Only partition when the kind actually has near
		// meshes (else everything is far — the pure-billboard case).
		let nearInstances: SceneryInstance[] = instances;
		let farInstances: SceneryInstance[] = [];
		const nearCap = nearMeshMaxInstances > 0 ? nearMeshMaxInstances : 0;
		if (st.nearMeshes && nearCap > 0 && nearMeshMaxDistance > 0 && n > 0) {
			const distSq = (x: number, y: number): number => {
				const dx = x - cameraPos.x, dy = y - cameraPos.y;
				return dx * dx + dy * dy;
			};
			const nearMaxSq = nearMeshMaxDistance * nearMeshMaxDistance;
			// Collect instances within the radius, keeping the nearest `nearCap` (selection by
			// distance; ties broken by order). This is O(n) with a bounded heap of size `nearCap`.
			const selected: SceneryInstance[] = [];
			const push = (inst: SceneryInstance, d2: number): void => {
				if (selected.length < nearCap) {
					selected.push(inst);
					selected.sort((a, b)=>distSq(a.x, a.y) - distSq(b.x, b.y));
					return;
				}
				if (d2 < distSq(selected[selected.length - 1].x, selected[selected.length - 1].y)) {
					selected[selected.length - 1] = inst;
					selected.sort((a, b)=>distSq(a.x, a.y) - distSq(b.x, b.y));
				}
			};
			for (const inst of instances) {
				const d2 = distSq(inst.x, inst.y);
				if (d2 <= nearMaxSq) push(inst, d2);
			}
			nearInstances = selected;
			// Far = every instance NOT selected as near. The set is small (≤ n), so a Set is fine.
			if (nearInstances.length > 0) {
				const nearIds = new Set<SceneryInstance>(nearInstances);
				farInstances = instances.filter(inst=>!nearIds.has(inst));
			} else {
				farInstances = instances;
			}
			// Fill the near-tier real meshes FIRST (their buffers + counts are independent of the
			// billboard's).
			fillNearTierMeshes(st, nearInstances);
		} else {
			farInstances = instances;
		}

		const bill = st.billboardMesh;
		const billGeo = bill.geometry as THREE.InstancedBufferGeometry;
		const variantAttr = billGeo.getAttribute("instanceVariant") as THREE.InstancedBufferAttribute;
		const tintAttr = billGeo.getAttribute("instanceTint") as THREE.InstancedBufferAttribute;
		const growAttr = (attr: THREE.InstancedBufferAttribute, name: string): THREE.InstancedBufferAttribute =>{
			if (attr.array.length < n) {
				attr = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
				attr.setUsage(THREE.DynamicDrawUsage);
				billGeo.setAttribute(name, attr);
			}
			return attr;
		};
		const variantAttrG = growAttr(variantAttr, "instanceVariant");
		const tintAttrG = growAttr(tintAttr, "instanceTint");
		if (bill.instanceMatrix == null || bill.instanceMatrix.array.length < n * 16) {
			bill.instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(n * 16), 16);
			bill.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
		}
		// The billboard draws the FAR instances; its count is the far count (0 when everything is
		// near). Buffers stay sized to n (the union), so they only grow as the union grows.
		const m2 = farInstances.length;
		bill.count = m2;
		const matrixArr = bill.instanceMatrix.array;
		for (let i = 0; i < m2; i++) {
			const inst = farInstances[i];
			const scl = quadHalfExtent * inst.scale;
			// The terrain height at the tree base (the D-less displacement lattice — matches the
			// surface the displaced terrain renders at). The billboard's cell bottom (the tree's
			// ground in the pitched bake) must sit exactly here. A small Z OFFSET (0.1 m) lifts
			// the base just above the terrain so the billboard wins the depth test against the
			// terrain surface at the same height (2026-09-01: at a near-horizontal pitch the
			// billboard base and the terrain are at nearly equal depth over a long view distance
			// → z-fighting → the terrain wins LessEqual → the billboard vanishes).
			const baseZ = sampleTerrainHeightForScenery(inst.x, inst.y) + 0.1;
			// CYLINDRICAL billboard: rotate about z so the quad's FRONT face (its visible side,
			// normal = local −Y per `billboardGeometry3D`'s winding) faces the camera's horizontal
			// azimuth. The camera is LOCKED (static viewpoint) so this is computed once per
			// instance per frame, cheaply. Trunks stay vertical (world up = +z).
			// ⚠ SIGN (2026-09-01): the quad's front-face normal is local −Y, and Rz(θ) maps it to
			// world (sin θ, −cos θ). To aim it AT the camera (direction (dx, dy)/|·| from the tree),
			// we need (sin θ, −cos θ) = (dx, dy)/|·| ⇒ θ = atan2(dx, −dy). The OLD `atan2(dx, dy)`
			// produced (dx/|·|, −dy/|·|) — the BACK of the quad toward the camera — so at a
			// horizontal azimuth the front face pointed AWAY and FrontSide culled ~95% of trees
			// (only ones nearly edge-on, |dot|≈0, leaked through). The `−dy` (NOT `+dy`) is the fix.
			const dx = cameraPos.x - inst.x;
			const dy = cameraPos.y - inst.y;
			const az = Math.atan2(dx, -dy);
			// The cell spans ±scl vertically from the base → translate z = baseZ + scl (so the
			// quad's bottom = baseZ, top = baseZ + 2·scl). Scale (scl, scl, scl) makes the
			// vertical XZ quad span ±scl both horizontally and vertically.
			p.set(inst.x, inst.y, baseZ + scl);
			q.setFromAxisAngle(axis, az);
			s.set(scl, scl, scl);
			m.compose(p, q, s);
			matrixArr.set(m.elements, i * 16);
			variantAttrG.array[i] = inst.variantIndex;
			tintAttrG.array[i] = inst.tint;
		}
		bill.instanceMatrix.needsUpdate = true;
		variantAttrG.needsUpdate = true;
		tintAttrG.needsUpdate = true;
		return;
	}

	// Ensure the per-instance attributes + instance matrices are at least capacity n (grow-only).
	const treeGeo = st.treeMesh.geometry as THREE.InstancedBufferGeometry;
	let scaleAttr = treeGeo.getAttribute("instanceScale") as THREE.InstancedBufferAttribute | undefined;
	let variantAttr = treeGeo.getAttribute("instanceVariant") as THREE.InstancedBufferAttribute | undefined;
	let depthAttr = treeGeo.getAttribute("instanceDepthKey") as THREE.InstancedBufferAttribute | undefined;
	let tintAttr = treeGeo.getAttribute("instanceTint") as THREE.InstancedBufferAttribute | undefined;
	const growAttr = (attr: THREE.InstancedBufferAttribute | undefined, name: string): THREE.InstancedBufferAttribute =>{
		if (attr == null || attr.array.length < n) {
			attr = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
			attr.setUsage(THREE.DynamicDrawUsage);
			treeGeo.setAttribute(name, attr);
		}
		return attr;
	};
	scaleAttr = growAttr(scaleAttr, "instanceScale");
	variantAttr = growAttr(variantAttr, "instanceVariant");
	depthAttr = growAttr(depthAttr, "instanceDepthKey");
	tintAttr = growAttr(tintAttr, "instanceTint");
	// Instance matrices: grow-or-reuse (reuse keeps the buffer from churning; grow only when
	// capacity is exceeded). Re-assigning a new InstancedBufferAttribute each frame is what
	// churned; instead grow in place.
	if (st.treeMesh.instanceMatrix == null || st.treeMesh.instanceMatrix.array.length < n * 16) {
		st.treeMesh.instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(n * 16), 16);
		st.treeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
	}
	if (st.shadowMesh.instanceMatrix == null || st.shadowMesh.instanceMatrix.array.length < n * 16) {
		st.shadowMesh.instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(n * 16), 16);
		st.shadowMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
	}
	st.treeMesh.count = n;
	st.shadowMesh.count = n;
	const matrixArr = st.treeMesh.instanceMatrix.array;
	const shadowMatrixArr = st.shadowMesh.instanceMatrix.array;

	// Shadow shear length down-light: variantMaxZ * scale * cot(alt), where cot(alt) = |L.xy|/L.z
	// (L points TOWARD the sun — the terrain hillshade convention — but a shadow extends AWAY from
	// the sun, so the shear runs the NEGATED light direction). The negation happens in the shader:
	// `u_lightDir` is set to `-lightDir` in setKindFrameUniforms, so the down-light unit vector D
	// below is `-L.xy/|L.xy|` and the per-instance shear length uses `|L.xy|/L.z`.
	const invZ = lightDir[2] > 1e-4 ? 1 / lightDir[2] : 1;
	const horizLen = Math.hypot(lightDir[0], lightDir[1]);
	const cotAlt = horizLen > 1e-4 ? horizLen * invZ : 0;

	// Shadow per-instance attributes: variant (cell select), crownHalf (the same scl the tree
	// quad uses — so the shadow can never drift), shearLen (the down-light tail length).
	const shadowGeo = st.shadowMesh.geometry as THREE.InstancedBufferGeometry;
	let shadowVariantAttr = shadowGeo.getAttribute("instanceVariant") as THREE.InstancedBufferAttribute | undefined;
	let shadowCrownHalfAttr = shadowGeo.getAttribute("instanceCrownHalf") as THREE.InstancedBufferAttribute | undefined;
	let shadowShearAttr = shadowGeo.getAttribute("instanceShearLen") as THREE.InstancedBufferAttribute | undefined;
	const growShadowAttr = (attr: THREE.InstancedBufferAttribute | undefined, name: string): THREE.InstancedBufferAttribute =>{
		if (attr == null || attr.array.length < n) {
			attr = new THREE.InstancedBufferAttribute(new Float32Array(n), 1);
			attr.setUsage(THREE.DynamicDrawUsage);
			shadowGeo.setAttribute(name, attr);
		}
		return attr;
	};
	shadowVariantAttr = growShadowAttr(shadowVariantAttr, "instanceVariant");
	shadowCrownHalfAttr = growShadowAttr(shadowCrownHalfAttr, "instanceCrownHalf");
	shadowShearAttr = growShadowAttr(shadowShearAttr, "instanceShearLen");

	// The shadow quad's local +x axis must align with the down-light direction so the fragment's
	// `along` axis maps onto it, and the rectangle must be ASYMMETRIC (up-light half = crownHalf,
	// down-light half = crownHalf + shearLen) to cover exactly the fragment's shear range. A
	// Matrix4.compose builds T·R·S (scale applies to LOCAL axes BEFORE the rotation), so the
	// elongated axis (local x) must be the one the rotation turns into the down-light direction.
	//
	// ⚠ Two failure modes this shape avoids:
	//   1. (Old) the elongation was put on local +y — which a z-rotation maps PERPENDICULAR to the
	//      light, so the crown's shear tail was clipped at exactly crownHalf → the hard cut-off.
	//   2. (Old) the quad was CENTRED on the tree with crownHalf+shearLen on BOTH light-axis sides
	//      → the up-light half overshot by shearLen and the fragment's cross-light uv swept past
	//      the cell into the neighbour variant's crown → the phantom "second/third shadows".
	// The fragment maps along = dot(q,D) ∈ [-crownHalf, crownHalf+shearLen] → cell uv [0,1]²
	// exactly, so this geometry + the crownAlpha cell-guard keep the sample confined to its cell.
	const shadowAngle = Math.atan2(-lightDir[1], -lightDir[0]); // local +x → down-light dir D
	const dx = Math.cos(shadowAngle); // D.x
	const dy = Math.sin(shadowAngle); // D.y

	// 2026-08-28 — the 2D fill loop previously called `Matrix4.compose` + `Quaternion.setFromAxisAngle`
	// (trig + full 4×4 multiply) TWICE per instance (tree + shadow). At ~200 k instances that's
	// ~400 k matrix constructions/frame — the `fillKindInstances` hotspot. Every transform here is a
	// z=0 plane translate + z-rotation + non-uniform scale, which has a CLOSED FORM (column-major
	// `elements`, same order THREE's Matrix4.compose produces):
	//   T( tx,ty,tz ) · Rz(θ) · S( sx,sy,sz ) =
	//   [ cos·sx, -sin·sy, 0, tx ]      elements = [c·sx, s·sx, 0, 0,  -s·sy, c·sy, 0, 0,  0, 0, sz, 0,  tx, ty, tz, 1]
	//   [ sin·sx,  cos·sy, 0, ty ]      (m11,m21,m31,m41, m12,m22,m32,m42, m13,m23,m33,m43, m14,m24,m34,m44)
	//   [ 0,       0,      sz, tz ]
	//   [ 0,       0,      0,  1  ]
	// Writing the 16 floats directly avoids the trig `setFromAxisAngle` + `Matrix4.compose`
	// multiply per instance (byte-identical matrices).
	const setPlaneMatrix = (arr: Float32Array, off: number, tx: number, ty: number, tz: number, c: number, s: number, sx: number, sy: number, sz: number): void => {
		arr[off] = c * sx; arr[off+1] = s * sx; arr[off+2] = 0; arr[off+3] = 0;
		arr[off+4] = -s * sy; arr[off+5] = c * sy; arr[off+6] = 0; arr[off+7] = 0;
		arr[off+8] = 0; arr[off+9] = 0; arr[off+10] = sz; arr[off+11] = 0;
		arr[off+12] = tx; arr[off+13] = ty; arr[off+14] = tz; arr[off+15] = 1;
	};

	for (let i = 0; i < n; i++) {
		const inst = instances[i];
		// 2D tree quad: uniform square scale (pitch=90 → the baker forces cellWorldHalfExtentZ = X; 2D ignores Z, uses X only).
		const scl = quadHalfExtent * inst.scale;
		const maxZ = (variantMaxZ[inst.variantIndex] ?? 1) * inst.scale;
		const shearLen = maxZ * cotAlt;

		// Tree: translate (x, y, 0) + rotate about z + uniform scale scl (sx=sy=scl, sz=1).
		const tc = Math.cos(inst.rotation);
		const ts = Math.sin(inst.rotation);
		setPlaneMatrix(matrixArr as Float32Array, i * 16, inst.x, inst.y, 0, tc, ts, scl, scl, 1);

		// Shadow: an asymmetric rectangle anchored on the tree base. Local +x runs the full
		// down-light span [−crownHalf, +crownHalf+shearLen] (half-extent crownHalf+shearLen/2),
		// local +y is the crown's across-extent (half-extent crownHalf). The centre is offset
		// shearLen/2 down-light from the tree base so the base ring stays put and the apex casts
		// the long tail (the fragment shears the crown mask; this is the quad that bounds it).
		const halfAlong = scl + shearLen * 0.5;
		setPlaneMatrix(shadowMatrixArr as Float32Array, i * 16, inst.x + dx * shearLen * 0.5, inst.y + dy * shearLen * 0.5, 0, dx, dy, halfAlong, scl, 1);

		scaleAttr.array[i] = scl;
		variantAttr.array[i] = inst.variantIndex;
		// Crown-top ordering key: the MEASURED variant crown-top (× scale) — the honest per-variant
		// top height (the placer's `inst.depthKey` is a targetHeight-based fallback only).
		depthAttr.array[i] = maxZ;
		tintAttr.array[i] = inst.tint;
		shadowVariantAttr.array[i] = inst.variantIndex;
		shadowCrownHalfAttr.array[i] = scl;
		shadowShearAttr.array[i] = shearLen;
	}

	st.treeMesh.instanceMatrix.needsUpdate = true;
	st.shadowMesh.instanceMatrix.needsUpdate = true;
	scaleAttr.needsUpdate = true;
	variantAttr.needsUpdate = true;
	depthAttr.needsUpdate = true;
	tintAttr.needsUpdate = true;
	shadowVariantAttr.needsUpdate = true;
	shadowCrownHalfAttr.needsUpdate = true;
	shadowShearAttr.needsUpdate = true;
}

/** Set15 P7 (§3.8) — fill the NEAR-TIER real meshes from the near partition of a kind's
 *  instances. Each instance goes to its variant's bark + leaf `InstancedMesh` (the per-variant
 *  geometry + materials built at create time). Per-instance: translate (x, y, baseZ) (ground at
 *  the terrain height), rotate about z (the instance's yaw jitter), uniform scale by `inst.scale`.
 *  The per-variant `material.color` already carries the authored tint; per-instance `instanceColor`
 *  adds the free `inst.tint` jitter (rgb = tint³). All meshes are set to `count = n` for their
 *  variant (grow-only buffers, set exactly). */
function fillNearTierMeshes(st: KindRenderState, nearInstances: SceneryInstance[]): void {
	if (!st.nearMeshes) return;
	const {_matrix: m, _quat: q, _pos: p, _scl: s, _axis: axis} = st;
	// Set15 P8 (§6.2) — near-tier real meshes CAST + RECEIVE real shadows ONLY when a shadow light
	// is actually active (a `MeshStandardMaterial` handles three's built-in shadow path). Gated on
	// the module-global shadow light (NOT unconditional — see buildNearTierMeshes) so a leaked
	// `gl.shadowMap.enabled` with no shadow light never puts these meshes through a shadow-receive
	// path they don't need (a scenery-disappearing source).
	const shadowActive = terrainShadowLight_current != null;
	for (const nv of st.nearMeshes) {
		nv.bark.castShadow = shadowActive;
		nv.bark.receiveShadow = shadowActive;
		nv.leaf.castShadow = shadowActive;
		nv.leaf.receiveShadow = shadowActive;
	}
	// Group near instances by variant index so each variant's meshes get one contiguous buffer.
	// With ≤ `nearMeshMaxInstances` near instances and ≤ variantCount variants, this is cheap.
	const byVariant = new Map<number, SceneryInstance[]>();
	for (const inst of nearInstances) {
		const list = byVariant.get(inst.variantIndex);
		if (list) list.push(inst);
		else byVariant.set(inst.variantIndex, [inst]);
	}
	for (const nv of st.nearMeshes) {
		const list = byVariant.get(nv.variantIndex);
		const count = list ? list.length : 0;
		nv.bark.count = count;
		nv.leaf.count = count;
		if (count == 0) continue;
		// Grow-or-reuse instance matrices (never reallocated each frame past capacity).
		const growMatrix = (mesh: THREE.InstancedMesh, attr: THREE.InstancedBufferAttribute | null): THREE.InstancedBufferAttribute => {
			if (attr == null || attr.array.length < count * 16) {
				attr = new THREE.InstancedBufferAttribute(new Float32Array(count * 16), 16);
				attr.setUsage(THREE.DynamicDrawUsage);
				mesh.instanceMatrix = attr;
			}
			return attr;
		};
		const barkMat = growMatrix(nv.bark, nv.bark.instanceMatrix);
		const leafMat = growMatrix(nv.leaf, nv.leaf.instanceMatrix);
		// Grow-or-reuse instanceColor (rgb = per-instance tint³, MeshStandardMaterial's
		// USE_INSTANCING_COLOR path).
		const growColor = (mesh: THREE.InstancedMesh, attr: THREE.InstancedBufferAttribute | null): THREE.InstancedBufferAttribute => {
			if (attr == null || attr.array.length < count * 3) {
				attr = new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3);
				attr.setUsage(THREE.DynamicDrawUsage);
				mesh.instanceColor = attr;
			}
			return attr;
		};
		const barkColor = growColor(nv.bark, nv.bark.instanceColor as THREE.InstancedBufferAttribute | null);
		const leafColor = growColor(nv.leaf, nv.leaf.instanceColor as THREE.InstancedBufferAttribute | null);
		for (let i = 0; i < count; i++) {
			const inst = list![i];
			const baseZ = sampleTerrainHeightForScenery(inst.x, inst.y);
			// The mesh's ground (min-Z = 0, per Gen_EzTree grounding) sits at the terrain height.
			// Uniform scale by inst.scale (the tree's full size, since the mesh is already at its
			// resolved `targetHeight` — the bake's quad used `cellWorldHalfExtent * scale`, but the
			// real mesh's world size IS the baked geometry, so just `inst.scale`).
			p.set(inst.x, inst.y, baseZ);
			q.setFromAxisAngle(axis, inst.rotation);
			s.set(inst.scale, inst.scale, inst.scale);
			m.compose(p, q, s);
			barkMat.array.set(m.elements, i * 16);
			leafMat.array.set(m.elements, i * 16);
			const t = inst.tint;
			const o = i * 3;
			barkColor.array[o] = t; barkColor.array[o + 1] = t; barkColor.array[o + 2] = t;
			leafColor.array[o] = t; leafColor.array[o + 1] = t; leafColor.array[o + 2] = t;
		}
		barkMat.needsUpdate = true;
		leafMat.needsUpdate = true;
		barkColor.needsUpdate = true;
		leafColor.needsUpdate = true;
	}
}
